/**
 * V3ChatInterface - Chat interface for Agent V3
 *
 * Same UI as GlobalChatInterface (welcome page, MessageList, HectorPromptInputBox).
 * Only difference: messages are sent/received via WebSocket instead of SSE.
 */

import { Fragment, useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArchiveRestore, Loader2, FolderOpen, ChevronDown, WifiOff, RefreshCw, Briefcase, Eye } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { V3MessageList } from "./common/V3MessageList";
import { useSession } from "../hooks/useSession";
import { useWebSocketPool } from "../providers/WebSocketPoolProvider";
import { useActiveSessionsStore } from "../stores/activeSessionsStore";
import type { ContextCompactionState, SessionStatus } from "../types/session.types";
import { MAX_ACTIVE_SLOTS, isExchangeActive } from "../types/session.types";
import { StreamingTimeline } from "./common";
import { useAuth } from "@/components/AuthProvider";
import { useAllCases } from "@/hooks/useCases";
import { HectorPromptInputBox } from "@/components/case/HectorPromptInput";
import { apiClient } from "@/lib/apiClient";
import { usePostHog } from "@/hooks/usePostHog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import type { ChatMessage } from "@/types/chat";
import { historyLooksInFlight, lastUserMessage as findLastUserMessage } from "../utils/historyLooksInFlight";
import type { DocumentContextMultiFilter } from "@/types/documentContext";
import { DEFAULT_DOCUMENT_MULTI_CONTEXT } from "@/types/documentContext";
import { ErrorService } from "@/lib/errors";
import { useConversationCases } from "../hooks/useConversationCases";
import type { BrouillonComposeur } from "../state/brouillonComposeurRelais";
import type { ConversationCaseListItem, ConversationCaseRefusal } from "@hector/shared-types";
import { DEFAULT_TOOL_OPTIONS, mapToolOptionsToWS, type CaseContextMode } from "../types";
import { useConversationFeedback } from "../hooks/useConversationFeedback";
import { usePendingConversationSkipId } from "../hooks/usePendingConversationSkipId";
import type { AttachedDocument } from "../types/websocket.types";
import {
  describeRefusedAttachments,
  describeUnsentAttachments,
  uploadTurnAttachments,
  type UnsentAttachment,
} from "../utils/uploadTurnAttachments";
import { rendreFichiersAuComposeur, usePiecesJointesPoseesSurLeComposeur } from "../state/fichiersRendusAuComposeur";
import { lireConversationAffichee } from "../state/conversationAffichee";
import { useUserPrompts } from '../hooks/useUserPrompts';
import { promptCategory } from '../api/userPrompts.api';
import { useConversationHistory } from '../hooks/useConversationHistory';
import { useConversationMemoryUsage } from '../hooks/useConversationMemoryUsage';
import { ExchangeCitationProvider, mergeCitationRegistries } from './common/CitationRegistryContext';
import type {
  AgentUiActionId,
  CitationRegistry,
  ResolvedAgentUiAction,
} from '@hector/shared-types';
import { useWakeLock } from '@/hooks/useWakeLock';
import { buildTranscriptExchanges } from '../utils/buildTranscriptExchanges';
import { hasMeaningfulChatContent } from '../utils/hasMeaningfulChatContent';
import { findLastConversationExchangeIndex } from '../utils/findLastConversationExchangeIndex';
import { resolveDisplayExchangeCaseId } from '../types/displayExchange';
import { useConversationTranscriptStore } from '../stores/conversationTranscriptStore';
import { QuickActionsPanel } from './prompt-library/QuickActionsPanel';
import {
  applyViewportScroll,
  createChatScrollIntent,
  requestBottomFollow,
  shouldFollowContentGrowth,
  stopBottomFollow,
  type ChatScrollIntent,
} from '../utils/chatScrollBehavior';
import { toast } from "sonner";
import { toastDuration } from "@/components/ui/sonner";
import {
  acquireConversationSendLock,
  canSendToConversationTarget,
  canOfferConversationCompaction,
  canUseConversationComposerActions,
  getConversationSendLockIdentity,
  getConversationSendState,
  hasConversationSendInFlight,
  releaseConversationSendLock,
  resolveMatchingLiveSessionKey,
  setConversationSendUploadingFiles,
  shouldBlockComposerSubmission,
  usePendingConversationRouteOwnership,
  type ComposerAccessState,
  type ConversationComposerAccessSnapshot,
  type ConversationSendLocks,
} from '../utils/conversationComposerAccess';
import { CaseDiscoveryModelSelector } from './CaseDiscoveryModelSelector';
import { ConversationExportButton } from './shared/ConversationExportButton';
import {
  resolveComposerRouteTransition,
  type ConversationRouteOwner,
} from '../utils/conversationRouteOwnership';

/**
 * V2 (architecture 3 canaux, validée @user 2026-06-05) — le chat agent ne stream
 * PLUS jamais dans le canvas. Seuls 3 canaux écrivent : `research-only` SSE,
 * `generation-only` SSE, et les `canvas_*` tools (round-trip WS). L'ancien
 * pipeline « préfixe d'instructions + StructuralParser sur timeline.content »
 * a été retiré — la réponse du chat reste conversationnelle dans le chat.
 *
 * `stripRedactionPrefix` reste exporté UNIQUEMENT pour le nettoyage des
 * messages historiques en base qui avaient été persistés avec l'ancien
 * préfixe. Une fois les anciennes conversations rejetées (cf. DEBT à créer),
 * cette fonction peut être supprimée.
 */
export function stripRedactionPrefix(content: string): string {
  const re = /^\[INSTRUCTION_RÉDACTION\][\s\S]*?\[\/INSTRUCTION_RÉDACTION\]\s*/;
  return content.replace(re, '');
}

const SUGGESTED_TASKS = [
  "Synthétiser les faits du dossier",
  "Analyser les conclusions adverses",
  "Trouver des arguments pour défendre votre client",
  "Rechercher de la jurisprudence pertinente",
  "Identifier les moyens de droit applicables",
  "Préparer un plan de plaidoirie",
  "Résumer une décision de justice",
  "Rédiger une trame d'acte juridique",
];

export interface EmbeddedQuickAction {
  /** Stable host-owned identity used for selection and external handoffs. */
  id: string;
  /** Action label displayed to the attorney. */
  label: string;
  /** Concise explanation of what the action prepares. */
  description: string;
  /** Draft inserted in the composer; it is never sent from the action button. */
  prompt: string;
  /** Decorative icon rendered outside the button's accessible name. */
  icon?: ReactNode;
}

interface V3ChatInterfaceProps {
  /** Explicit owner of the active conversation identity; presentation never decides it. */
  conversationRouteOwner: ConversationRouteOwner;
  /** Le dossier de la page qui héberge ce chat, s'il y en a une. */
  hostCaseId?: string | null;
  conversationId: string | null;
  sessionKey: string | null;
  onSessionKeyChange: (key: string | null) => void;
  selectedCaseId: string | null;
  onCaseSelect: (caseId: string | null) => void;
  onConversationCreated?: (conversationId: string) => void;
  /**
   * Le premier message d'une conversation neuve n'est pas parti (téléversement
   * en échec, session refusée) : aucune ligne n'existe en base pour cet id.
   * L'hôte qui l'a déjà écrit dans l'URL doit l'en retirer.
   */
  onConversationAbandoned?: (conversationId: string, brouillon: BrouillonComposeur) => void;
  onStreamingStart?: () => void;
  onStreamingEnd?: () => void;
  /** Title of the open conversation, shown as a header above the messages. */
  conversationTitle?: string;
  /**
   * Repère posé par l'hôte dans la barre de titre, à gauche du nom du fil
   * (#1588 : « revenir d'où l'on vient » après une bascule venue du dock).
   *
   * Un emplacement, pas une surface de plus : la barre de titre existe déjà,
   * elle a une hauteur fixe et elle ne recouvre rien. Ignoré en mode embarqué,
   * où cette barre est masquée.
   */
  repereDeRetour?: ReactNode;
  /** Explicit access state resolved from the URL conversation detail. */
  composerAccessState: ComposerAccessState;
  /** Retries only the authoritative conversation-detail request. */
  onComposerAccessRetry: () => void;
  /** Personal archive state of an already-persisted open conversation. */
  conversationArchived?: boolean;
  /** Explicit user command; opening or mounting the chat never calls it. */
  onConversationArchivedChange?: (archived: boolean) => Promise<void>;
  /**
   * `locked` keeps Case Discovery inside the host dossier; `host_selectable`
   * lets a host preselect a non-null dossier that the attorney may change per
   * turn. Both are semantic protocol behavior; `embedded` is presentation only.
   */
  caseContextMode?: CaseContextMode;
  /**
   * Mode embarqué (2026-07-17) — le chat est monté DANS une autre surface (le
   * wizard « Tamponner mes pièces ») plutôt que sur la page `/assistant`.
   *
   * Purement cosmétique : masque le chrome plein-écran qui n'a pas de sens dans
   * un panneau contraint — écran d'accueil + `QuickActionsPanel` et la puce
   * « ouvrir le dossier », seul `navigate()` du composant, qui éjecterait
   * l'avocat hors du wizard. Le sélecteur normal reste disponible quand l'hôte
   * choisit explicitement `caseContextMode="host_selectable"`.
   * Ne change RIEN au protocole WS ni au cycle d'envoi.
   */
  embedded?: boolean;
  /**
   * Cet hôte AFFICHE-t-il le canvas de rédaction ? (#860)
   *
   * Deny-by-default, comme les actions UI : un hôte qui ne l'annonce pas ne
   * rend ni le bouton « Rédaction » du composeur, ni le bouton d'ouverture de
   * la carte de plan. Seul l'écran assistant offre un emplacement au canvas
   * (`CanvasSlot`) ; ailleurs, ces boutons basculaient un interrupteur dont
   * personne ne lisait l'état sur la page courante — le clic ne produisait
   * rien de visible.
   *
   * **Une déclaration de l'hôte, pas un fait constaté** : l'emplacement n'est
   * rendu QUE lorsque le canvas est déjà ouvert, donc le déduire à l'exécution
   * interdirait la première ouverture. Et surtout pas une corrélation avec
   * `embedded`, qui ne parle que de présentation.
   */
  canOpenCanvas?: boolean;
  /**
   * Brouillon posé par l'hôte dans le composer : texte **et** pièces jointes,
   * sans jamais l'envoyer. L'avocat garde toujours le geste explicite d'envoi —
   * il peut relire, corriger, retirer l'acte joint.
   *
   * Chaque nouvelle valeur **remplace** l'état du composer (texte et pièces
   * jointes) : deux choix successifs dans le sélecteur de source de
   * « Numéroter avec l'IA » ne doivent jamais cumuler deux actes. `null` = ne
   * rien poser (le composer garde ce que l'avocat y a écrit).
   *
   * Même contrat que `embeddedEmptyState.quickActions`, avec les pièces jointes
   * en plus : le codebase n'a plus d'auto-send.
   */
  composerDraft?: { content: string; files?: File[] } | null;
  /** Instantané du composeur local, lu par un hôte avant son démontage. */
  onComposerDraftChange?: (draft: { readonly content: string; readonly files: readonly File[] }) => void;
  /** Confirme que le relais one-shot a été copié dans l'état local. */
  onComposerDraftConsumed?: () => void;
  /** Envoi déjà confirmé par l'hôte, protégé contre le double effet StrictMode. */
  hostSubmission?: { content: string; nonce: number } | null;
  /**
   * Actions proposées par l'agent en fin de tour (frame `agent_ui_actions`),
   * rendues en boutons cliquables dans la timeline. L'hôte décide quoi faire —
   * le chat ne connaît que l'identifiant d'action.
   */
  onUiAction?: (action: ResolvedAgentUiAction) => Promise<void>;
  /**
   * Restreint les actions RENDUES à celles que l'hôte sait honorer. L'agent
   * choisit dans un catalogue commun, mais les hôtes n'offrent pas tous les mêmes
   * suites : sans ce filtre, une action hors périmètre produirait un bouton
   * cliquable qui ne fait rien. Requise, même vide : elle est aussi figée sur
   * chaque message WS.
   */
  uiActionCapabilities: readonly AgentUiActionId[];
  /**
   * Écran d'accueil du mode embarqué. Ne s'affiche QUE tant que la conversation
   * est vide : au premier message on retombe dans le layout de chat normal, et
   * tout ce que l'hôte a posé ici disparaît. Sans cette prop, le mode embarqué
   * se réduit au composer seul (grand vide au-dessus, cf. incident 2026-07-17).
   * L'hôte fournit son propre libellé et ses raccourcis : le chat générique ne
   * connaît rien du bordereau.
   */
  embeddedEmptyState?: {
    /** Titre principal. Défaut : « Comment puis-je vous aider ? ». */
    heading?: string;
    /** Sous-titre qui tourne (2,8 s) pour l'accueil décoratif de l'hôte. */
    rotatingTasks?: readonly string[];
    /**
     * Raccourcis fixes fournis par l'hôte. Leur clic ne fait que préremplir le
     * composer : l'avocat conserve toujours le geste explicite d'envoi.
     */
    quickActions?: readonly EmbeddedQuickAction[];
    /** Action prepared by an external host without sending it. */
    initialQuickActionId?: string;
    /**
     * Bloc de l'hôte inséré **entre le titre et le composer** — pour
     * « Numéroter avec l'IA », le sélecteur de source (déposer un acte /
     * choisir une écriture) qui alimente `composerDraft`.
     *
     * Il vit dans l'écran d'accueil, donc il disparaît au premier message : un
     * sélecteur de source n'a plus de sens une fois la conversation engagée
     * (l'avocat joint alors ses fichiers avec le trombone du composer).
     */
    beforeComposer?: ReactNode;
  };
}

/**
 * Bandeau « lecture seule » affiché à la place du composer pour un `viewer` d'un
 * fil partagé : l'historique reste consultable, mais aucun message ne peut être
 * envoyé (le backend re-garde l'écriture WS de toute façon, cf. Phase 4 T1).
 */
function ReadOnlyBanner({ conversationCases }: { conversationCases: readonly ConversationCaseListItem[] }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-lg border border-grey-200 bg-grey-50 px-4 py-3 text-sm text-grey-600">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 flex-shrink-0 text-grey-500" aria-hidden />
        <span>Vous consultez un fil partagé en lecture seule.</span>
      </div>
      {/* Le lecteur n'a pas de composeur : c'est ici qu'il voit les dossiers de la conversation. */}
      {conversationCases.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1">
          {conversationCases.map((item) => (
            <span
              key={item.case_id}
              title={`Dossier ${item.role === 'principal' ? 'principal' : 'connexe'} : ${item.title}`}
              className="max-w-[160px] truncate rounded-full border border-grey-200 px-2.5 py-0.5 text-xs text-grey-600"
            >
              {item.title}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ComposerAccessLoading() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-grey-200 bg-grey-50 px-4 py-3 text-sm text-grey-600" role="status">
      <Loader2 className="h-4 w-4 animate-spin text-grey-500" aria-hidden />
      <span>Chargement de l'accès à la conversation...</span>
    </div>
  );
}

function ComposerAccessUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center gap-3 rounded-lg border border-grey-200 bg-grey-50 px-4 py-3 text-sm text-grey-600">
      <span>Impossible de confirmer vos droits sur cette conversation.</span>
      <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" onClick={onRetry}>
        <RefreshCw className="h-3.5 w-3.5" aria-hidden />
        Réessayer
      </Button>
    </div>
  );
}

function ComposerAccessSlot({
  composerAccessState,
  onRetry,
  conversationCases,
  children,
}: {
  composerAccessState: ComposerAccessState;
  onRetry: () => void;
  conversationCases: readonly ConversationCaseListItem[];
  children: ReactNode;
}) {
  switch (composerAccessState) {
    case 'editable':
      return <>{children}</>;
    case 'read_only':
      return <ReadOnlyBanner conversationCases={conversationCases} />;
    case 'loading':
      return <ComposerAccessLoading />;
    case 'unavailable':
      return <ComposerAccessUnavailable onRetry={onRetry} />;
  }
}

/** Discrete reconnection indicator shown above the prompt box */
function ConnectionStatus({ hasSession, isConnected, reconnectInfo, reconnectFailed, sessionStatus, onRetry }: {
  hasSession: boolean;
  isConnected: boolean;
  reconnectInfo: { attempt: number; maxAttempts: number; countdown: number } | null;
  reconnectFailed: boolean;
  sessionStatus: SessionStatus | null;
  onRetry?: () => void;
}) {
  // Don't show anything before a session exists — connection is lazy (first message)
  if (!hasSession) return null;
  if (isConnected && !reconnectInfo && !reconnectFailed) return null;

  // Ready or error session states mean the WS disconnect is expected — the
  // connection will be re-established lazily on the next user message. Don't
  // alarm the user with a "Connexion perdue" banner for normal lifecycle events.
  const isInactiveOrTerminal = sessionStatus === 'ready' || sessionStatus === 'error';
  if (isInactiveOrTerminal && !reconnectInfo) return null;

  return (
    <div className="flex items-center justify-center gap-2 py-1.5 text-xs text-grey-500">
      {reconnectInfo && (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>
            Connexion au serveur {reconnectInfo.attempt}/{reconnectInfo.maxAttempts}
            {reconnectInfo.countdown > 0 && ` (${reconnectInfo.countdown}s)`}
          </span>
        </>
      )}
      {reconnectFailed && (
        <>
          <WifiOff className="h-3 w-3" />
          <span>Impossible de se connecter</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1 text-xs text-accent-blue hover:text-accent-blue/80 underline underline-offset-2"
            >
              <RefreshCw className="h-3 w-3" />
              Réessayer
            </button>
          )}
        </>
      )}
      {!isConnected && !reconnectInfo && !reconnectFailed && (
        <>
          <WifiOff className="h-3 w-3" />
          <span>Connexion perdue</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1 text-xs text-accent-blue hover:text-accent-blue/80 underline underline-offset-2"
            >
              <RefreshCw className="h-3 w-3" />
              Reconnecter
            </button>
          )}
        </>
      )}
    </div>
  );
}

function readScrollMetrics(el: HTMLElement) {
  return {
    scrollHeight: el.scrollHeight,
    scrollTop: el.scrollTop,
    clientHeight: el.clientHeight,
  };
}

/** Refus d'une compaction manuelle : un envoi ou une réponse occupe déjà ce fil. */
const COMPACTION_BLOCKED_BY_SEND_MESSAGE =
  "La compaction n'a pas pu démarrer : une réponse est en cours ou attend son envoi. Réessayez dans un instant.";

/** Rond qui tourne pendant l'ouverture de socket d'une compaction, avant que le pool ne porte l'état. */
const RUNNING_CONTEXT_COMPACTION: ContextCompactionState = Object.freeze({ status: 'running' });

export function V3ChatInterface({
  conversationRouteOwner,
  hostCaseId = null,
  conversationId,
  sessionKey,
  onSessionKeyChange,
  selectedCaseId,
  onCaseSelect,
  onConversationCreated,
  onConversationAbandoned,
  onStreamingStart,
  onStreamingEnd,
  conversationTitle,
  repereDeRetour,
  composerAccessState,
  onComposerAccessRetry,
  conversationArchived = false,
  onConversationArchivedChange,
  caseContextMode = 'selectable',
  embedded = false,
  canOpenCanvas = false,
  composerDraft = null,
  onComposerDraftChange,
  onComposerDraftConsumed,
  hostSubmission = null,
  onUiAction,
  uiActionCapabilities,
  embeddedEmptyState,
}: V3ChatInterfaceProps) {
  if (caseContextMode !== 'selectable' && !selectedCaseId) {
    throw new Error(
      `V3ChatInterface requires selectedCaseId when caseContextMode is "${caseContextMode}". Pass the host dossier ID instead of falling back to a general conversation.`,
    );
  }

  const navigate = useNavigate();
  const location = useLocation();
  const composerAccessSnapshotRef = useRef<ConversationComposerAccessSnapshot>({
    conversationId,
    accessState: composerAccessState,
  });
  composerAccessSnapshotRef.current = { conversationId, accessState: composerAccessState };
  const canUseComposerActions = canUseConversationComposerActions(composerAccessState);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [selectedEmbeddedQuickActionId, setSelectedEmbeddedQuickActionId] = useState<string | null>(
    () => embeddedEmptyState?.initialQuickActionId ?? null,
  );
  const [caseSearchQuery, setCaseSearchQuery] = useState("");

  const [documentContext, setDocumentContext] = useState<DocumentContextMultiFilter>(DEFAULT_DOCUMENT_MULTI_CONTEXT);
  const [suggestedTaskIndex, setSuggestedTaskIndex] = useState(0);
  const restoreRequestSequenceRef = useRef(0);
  const restoreRequestIdsRef = useRef(new Map<string, number>());
  const [restoringConversationIds, setRestoringConversationIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const pendingSendLocksRef = useRef<ConversationSendLocks>({});
  const [pendingSendLocks, setPendingSendLocks] = useState<ConversationSendLocks>({});
  const replacePendingSendLocks = useCallback((nextLocks: ConversationSendLocks) => {
    pendingSendLocksRef.current = nextLocks;
    setPendingSendLocks(nextLocks);
  }, []);
  const {
    pendingConversationId: emptyPagePendingConversationId,
    beginPendingConversation,
    clearPendingConversation,
    resolveRouteActivation,
  } = usePendingConversationRouteOwnership(conversationId, location.key);
  const { prompts: userPrompts, isLoading: promptsLoading, createPrompt, updatePrompt, setPromptHidden, deletePrompt, reorderPrompts } = useUserPrompts();
  // Le menu « Actions rapides » du composer ne connaît qu'une liste à plat,
  // sans sélecteur de dossier ni verrou des natives : il reste limité à la
  // colonne Analyse (comportement d'avant les colonnes). Son glisser-déposer
  // envoie donc une permutation de la seule colonne Analyse, que le serveur
  // accepte telle quelle.
  const composerQuickActions = useMemo(
    () => userPrompts.filter((prompt) => promptCategory(prompt) === 'analyse' && prompt.is_hidden !== true),
    [userPrompts],
  );
  // The optimistic row is the single lifecycle authority for REST hydration.
  // This remains stable while the WebSocket moves to `streaming`, then releases
  // exactly once when the global API list observes the persisted conversation.
  const pendingConversationSkipId = usePendingConversationSkipId(conversationId);

  const { user } = useAuth();
  const { trackEvent } = usePostHog();

  // Votes (pouce) déjà donnés par l'utilisateur sur cette conversation — rallume
  // les pouces à l'ouverture (sinon ils repartent neutres alors que le vote existe).
  const feedbackMap = useConversationFeedback(conversationId, {
    skipConversationId: pendingConversationSkipId,
  });

  // WebSocket session via pool (V3 multitasking)
  const pool = useWebSocketPool();
  const displayConversationId = conversationId ?? emptyPagePendingConversationId;
  const displayedSendState = getConversationSendState(pendingSendLocks, displayConversationId);
  const isDisplayedConversationSendPending = displayedSendState !== null;
  const uploadingFilenames = displayedSendState?.uploadingFilenames ?? [];
  // A pending id only gives optimistic display feedback while a first session
  // opens. It is never a route identity: a blank `/assistant` page must not
  // borrow the previously supplied key during the render before AgentV3View
  // detaches it, otherwise A's stream prevents B's first message from starting.
  const sessionConversationId = conversationId;
  const matchingActiveSessionKey = resolveMatchingLiveSessionKey({
    sessionKey,
    targetConversationId: sessionConversationId,
    getSessionStatus: (key) => pool.getSessionStatus(key),
    getConversationId: (key) => pool.getConversationId(key),
  });
  const suppliedSessionKeyRef = useRef(sessionKey);
  suppliedSessionKeyRef.current = sessionKey;
  const sessionConversationIdRef = useRef(sessionConversationId);
  sessionConversationIdRef.current = sessionConversationId;
  const getCurrentMatchingSessionKey = useCallback(() => resolveMatchingLiveSessionKey({
    sessionKey: suppliedSessionKeyRef.current,
    targetConversationId: sessionConversationIdRef.current,
    getSessionStatus: (key) => pool.getSessionStatus(key),
    getConversationId: (key) => pool.getConversationId(key),
  }), [pool]);
  const session = useSession(matchingActiveSessionKey ?? 'no-session');
  const {
    cancelStream,
    retryConnection,
    sendJurisprudenceResponse,
    sendJurisprudenceCancellation,
    sendCaseWikiResetResponse,
    sendDeletePiecesConfirmationResponse,
    sendSkillChangeResponse,
  } = session;

  // Determine si AU MOINS une recherche JP est en cours dans la session LIVE
  // (le timeline peut contenir plusieurs `jurisprudence_progress` simultanés
  // car MasterLoop.handleToolCall batche les jobs via JpJobCollector.collectAll
  // avec Promise.allSettled). On maintient le Wake Lock tant qu'il reste au
  // moins un progress non terminé.
  //
  // WHY `.some()` au lieu d'un reverse-scan : avec des jobs parallèles, le
  // reverse-scan voyait `results(A)` avant `progress(B)` et relâchait le
  // Wake Lock alors que B tournait encore — exactement le failure mode que
  // ce PR vise à empêcher (ultrareview bug 001 2026-04-24).
  //
  // WHY local au composant (pas dans useSession) : le flag ne considère QUE
  // le timeline live. StreamingSessionReducer.finalize() strip les orphans
  // sur `agent_done`, et les exchanges historiques restaurés depuis la DB
  // vivent dans `historicalExchanges`, pas dans `session.timeline`.
  //
  // WHY : protège le tab d'être gelé par Chrome/Safari pendant une recherche
  // JP longue (4+ min en deep). Sans Wake Lock, un tab backgroundé peut être
  // throttled → heartbeats manqués → WS morte → grace period expirée
  // (incident 2026-04-24).
  const isJpSearchActive = useMemo(
    () => session.timeline.some(item => item?.type === 'jurisprudence_progress'),
    [session.timeline]
  );

  useWakeLock(isJpSearchActive);

  const transcript = useConversationTranscriptStore(
    // A transcript has authority to block a turn, unlike the optimistic display
    // state above. It must therefore follow the URL-owned conversation only:
    // carrying A's active turn onto a blank route would reject B before its
    // own session can be admitted.
    (state) => (conversationId ? state.transcripts[conversationId] : undefined),
  );
  const hydrateTranscript = useConversationTranscriptStore((state) => state.hydrateTranscript);
  const beginTurn = useConversationTranscriptStore((state) => state.beginTurn);
  const bindOwnerSession = useConversationTranscriptStore((state) => state.bindOwnerSession);
  const rollbackTurn = useConversationTranscriptStore((state) => state.rollbackTurn);
  const replaceActiveTurnUserMessage = useConversationTranscriptStore((state) => state.replaceActiveTurnUserMessage);
  // A queued/auth-pending first turn has no `session.isStreaming` yet. The
  // transcript is its durable UI authority, so it also blocks a second composer
  // submit until the transport either starts or rolls back that exact turn.
  const hasActiveTranscriptTurn = transcript?.activeTurn !== null && transcript?.activeTurn !== undefined;
  // `window.location` changes before React has committed the new route. During
  // that one-render gap, A can still be streaming in the persistent composer
  // while the lawyer is already on blank route B. Do not let A's lock swallow
  // B's first Enter; `handleComposerSend` below waits for the current handler.
  // Only a URL-owned thread reads it: a host-owned thread never writes `?c=`.
  const browserConversationId = new URLSearchParams(window.location.search).get('c');
  // Une compaction manuelle n'est pas un tour (la session reste `ready`), mais le
  // backend tient le verrou de conversation pendant qu'elle tourne : un message
  // envoyé maintenant serait rejeté en `conversation_busy`. On bloque donc
  // l'envoi comme pendant une réponse — y compris pendant l'ouverture de la
  // socket d'une conversation rouverte, avant que le pool ne porte l'état.
  const [compactionOpeningConversationId, setCompactionOpeningConversationId] = useState<string | null>(null);
  // Garde de double clic lue sans attendre un rendu : l'état ci-dessus ne sert qu'à l'affichage.
  const compactionOpeningConversationIdRef = useRef<string | null>(null);
  const isOpeningSessionForCompaction = conversationId !== null && compactionOpeningConversationId === conversationId;
  const isCompactingContext = isOpeningSessionForCompaction || session.compaction.status === 'running';
  const shouldBlockCurrentComposer = shouldBlockComposerSubmission({
    conversationRouteOwner,
    renderedConversationId: conversationId,
    browserConversationId,
    hasCurrentConversationWork: session.isStreaming
      || hasActiveTranscriptTurn
      || isDisplayedConversationSendPending
      || isCompactingContext,
  });

  // --- Conversation history from DB ---
  // The REST read is now only the initial/base hydration. Once a turn is local,
  // `agent_done` commits it in the transcript store; we never force a terminal GET.
  const { messages: dbMessages, historicalExchanges, isLoading: isLoadingHistory, isHistoryReady, historyFetchedAt } = useConversationHistory(
    conversationId,
    { skipConversationId: pendingConversationSkipId },
  );
  // Jauge persistée : une conversation rouverte n'a pas de session live, donc pas de
  // `context_usage_update` — sans elle le rond restait vide jusqu'au premier échange.
  // La valeur live prime dès qu'elle existe (plus récente).
  const persistedMemoryUsage = useConversationMemoryUsage(
    conversationId,
    { skipConversationId: pendingConversationSkipId },
  );
  const memoryUsage = session.memoryUsage ?? persistedMemoryUsage;

  // Conversation-scoped citation registry: merge every persisted message's
  // `metadata.citationRegistry` so HectorLink (rendered deep under StreamingTimeline)
  // can resolve `<hector-link ref="…">` tokens. Provided once around the whole
  // exchange list below. Live-turn tokens are merged into the transcript by the
  // terminal snapshot bridge without refetching this hook.
  const dbCitationRegistry = useMemo(
    () => mergeCitationRegistries(
      ...dbMessages.map((m) => (m.metadata as { citationRegistry?: CitationRegistry } | undefined)?.citationRegistry),
    ),
    [dbMessages],
  );

  // --- Reprise au chargement (2026-09-28) ---
  // Une page neuve (F5, second onglet, autre poste) ne sait pas qu'un tour tourne : la
  // socket ne s'ouvre qu'au premier envoi et le composer s'ouvrait sur un tour en cours,
  // d'où « une réponse est déjà en cours » puis, au pire, un renvoi qui REMPLAÇAIT le tour
  // (logs prod 2026-09-24). Indice : l'historique se termine sans réponse finale. Décision
  // prise UNE fois par (conversation, dernière ligne) — jamais rejouée sur le même
  // historique, sinon un tour interrompu bouclerait (reprise → rien → rechargement → reprise).
  const historyInFlight = useMemo(() => historyLooksInFlight(dbMessages), [dbMessages]);
  const historyLastMessageId = dbMessages[dbMessages.length - 1]?.id ?? null;
  const resumeAttemptedKeysRef = useRef<Set<string>>(new Set());
  const [resumeOnLoad, setResumeOnLoad] = useState<{
    conversationId: string;
    lastMessageId: string | null;
    decision: 'attempted' | 'skipped';
    localTurnId: string;
    userMessage: ChatMessage | null;
  } | null>(null);
  const resumeKey = conversationId ? `${conversationId}:${historyLastMessageId ?? 'empty'}` : null;
  const resumeDecided = resumeOnLoad !== null
    && resumeOnLoad.conversationId === conversationId
    && resumeOnLoad.lastMessageId === historyLastMessageId;
  useEffect(() => {
    if (!conversationId || !isHistoryReady || !resumeKey || resumeDecided) return;
    const canAttempt = historyInFlight
      && !matchingActiveSessionKey
      && !!user
      && !resumeAttemptedKeysRef.current.has(resumeKey);
    if (canAttempt) resumeAttemptedKeysRef.current.add(resumeKey);
    setResumeOnLoad({
      conversationId,
      lastMessageId: historyLastMessageId,
      decision: canAttempt ? 'attempted' : 'skipped',
      localTurnId: crypto.randomUUID(),
      userMessage: canAttempt ? findLastUserMessage(dbMessages) : null,
    });
  }, [conversationId, dbMessages, historyInFlight, historyLastMessageId, isHistoryReady, matchingActiveSessionKey, resumeDecided, resumeKey, user]);

  const resumeStartedForRef = useRef<string | null>(null);
  const activeTurnId = transcript?.activeTurn?.localTurnId ?? null;
  useEffect(() => {
    if (!conversationId || !isHistoryReady) return;
    // Un historique « en cours » attend la décision de reprise : hydrater avant elle
    // afficherait l'échange sans réponse, puis le tour repris l'afficherait une 2e fois.
    if (historyInFlight && !resumeDecided) return;
    // Tour repris : l'échange final (message user sans réponse) est PORTÉ par le tour
    // local ouvert juste après (l'effet suivant, même commit), pas par la base. Le
    // retrait ne vaut que TANT QUE ce tour vit : à `turn_settled`, il est rollbacké et
    // le transcript marqué périmé ; si la base n'a pas changé (tour distant terminé
    // sans réponse persistée : redémarrage, Stop), la question de l'avocat ne serait
    // plus portée par personne. On la rend alors à la base (revue 2026-09-28).
    const resumeTurnId = resumeDecided && resumeOnLoad?.decision === 'attempted' ? resumeOnLoad.localTurnId : null;
    const resumeTurnEnded = resumeTurnId !== null
      && resumeStartedForRef.current === resumeTurnId
      && activeTurnId !== resumeTurnId;
    const exchanges = resumeTurnId !== null && !resumeTurnEnded
      ? historicalExchanges.slice(0, -1)
      : historicalExchanges;
    // `historyFetchedAt` : le store n'accepte de lever un marquage « périmé » que sur
    // une lecture postérieure au marquage. Il est aussi dans les dépendances : un
    // refetch qui rend des données identiques garde la même référence et doit quand
    // même rejouer l'effet pour lever le marquage.
    hydrateTranscript(conversationId, exchanges, dbCitationRegistry, historyFetchedAt);
  }, [activeTurnId, conversationId, dbCitationRegistry, historicalExchanges, historyFetchedAt, historyInFlight, hydrateTranscript, isHistoryReady, resumeDecided, resumeOnLoad]);

  useEffect(() => {
    if (!resumeDecided || !resumeOnLoad || resumeOnLoad.decision !== 'attempted') return;
    if (resumeStartedForRef.current === resumeOnLoad.localTurnId) return;
    if (!user || !conversationId || !resumeOnLoad.userMessage) return;
    resumeStartedForRef.current = resumeOnLoad.localTurnId;
    const { localTurnId, userMessage } = resumeOnLoad;
    const resumeCaseId = selectedCaseId ?? userMessage.caseId ?? null;
    // Le tour local projette la timeline (buildTranscriptExchanges ne peint rien sans
    // activeTurn) ; son message user est celui de la base, réaffiché à l'identique.
    if (!beginTurn(conversationId, localTurnId, { ...userMessage, conversationId }, resumeCaseId, [...uiActionCapabilities])) return;
    void (async () => {
      let key: string | null = null;
      try {
        key = await pool.openSessionForResume(user.id, conversationId, resumeCaseId ?? undefined, caseContextMode, localTurnId);
      } catch (error) {
        ErrorService.handle(error, { silent: true, context: { action: 'resume_turn_on_load', conversation_id: conversationId } });
      }
      if (!key) {
        rollbackTurn(conversationId, localTurnId);
        return;
      }
      useActiveSessionsStore.getState().createSession(key, conversationId, resumeCaseId);
      bindOwnerSession(conversationId, key);
      // L'avocat a pu changer de conversation pendant la poignée de main : la session
      // de reprise vit en arrière-plan (elle sera retrouvée par la réattache au retour),
      // mais on ne l'impose pas comme clé active d'une autre conversation.
      if (sessionConversationIdRef.current === conversationId) onSessionKeyChange(key);
    })();
  }, [beginTurn, bindOwnerSession, caseContextMode, conversationId, onSessionKeyChange, pool, resumeDecided, resumeOnLoad, rollbackTurn, selectedCaseId, uiActionCapabilities, user]);

  const citationRegistry = transcript?.citationRegistry ?? dbCitationRegistry;

  // --- Conversation creation tracking ---
  const handleConversationCreated = useCallback((newConvId: string) => {
    onConversationCreated?.(newConvId);
  }, [onConversationCreated]);

  // WHY no effect promoting session.conversationId → URL: navigation to the new
  // conversation's `?c=<id>` is written EXPLICITLY in handleSendMessage (the empty
  // assistant page creates the session there and knows the frontend-generated UUID
  // synchronously). An effect keyed on (session.conversationId, conversationId)
  // cannot tell "session just created for THIS empty page" from "stale sessionKey
  // of the previous conversation during navigation to a fresh page / another conv"
  // — for one render after navigation, sessionKey still points at the previous
  // (often streaming) session. Acting on that transient shoved the URL back to the
  // streaming conversation, breaking both "switch while streaming" AND "Nouvelle
  // conversation while streaming". The URL is the SSOT; only AgentV3View's reattach
  // effect changes which session is bound from a URL change. (#238 regression)

  const { data: cases = [], isLoading: isCasesLoading } = useAllCases();

  const caseTitles = useMemo(() => new Map(cases.map((c) => [c.id, c.title])), [cases]);
  const titreDuDossier = useCallback((caseId: string) => caseTitles.get(caseId), [caseTitles]);
  const conversationCases = useConversationCases({
    conversationId,
    hote: conversationRouteOwner,
    hostCaseId,
    selectedCaseId,
    onCaseSelect,
    enabled: caseContextMode !== 'locked',
    allowsCaseClear: caseContextMode === 'selectable',
    titreDe: titreDuDossier,
  });
  // Titre d'un dossier lu par l'agent : la liste de la conversation d'abord (elle
  // nomme aussi un connexe ajouté par l'agent), puis les dossiers du cabinet.
  const caseTitleOf = useCallback(
    (caseId: string) => conversationCases.liste?.find((item) => item.case_id === caseId)?.title ?? caseTitles.get(caseId),
    [conversationCases.liste, caseTitles],
  );
  // Un lecteur n'a aucun contrôle sur la liste. Le dock passe `editable` sans
  // connaître le rôle : le détail tranche pour lui aussi.
  const allowsHumanCaseSelection = caseContextMode !== 'locked' && !conversationCases.lecteur;
  const selectedCase = cases.find(c => c.id === selectedCaseId);
  // La liste nomme aussi un dossier que `useAllCases` ne rend pas : un principal
  // posé par l'agent ne doit pas laisser une pastille « Sélectionner un dossier ».
  const composerCase = selectedCase
    ? { id: selectedCase.id, title: selectedCase.title }
    : conversationCases.principal?.case_id === selectedCaseId && conversationCases.principal
      ? { id: conversationCases.principal.case_id, title: conversationCases.principal.title }
      : null;
  const composerRelatedCases = {
    relatedCases: conversationCases.connexes,
    onRelatedCaseAdd: allowsHumanCaseSelection ? conversationCases.ajouterConnexe : undefined,
    onRelatedCaseRemove: allowsHumanCaseSelection ? conversationCases.retirerConnexe : undefined,
  };
  const filteredCases = cases.filter(c =>
    c.title.toLowerCase().includes(caseSearchQuery.toLowerCase()) ||
    c.client_name?.toLowerCase().includes(caseSearchQuery.toLowerCase())
  );

  // --- Unified exchange pipeline ---
  // DB history, locally committed turns, and the active pending/live turn all
  // render through the transcript projection. This removes the terminal DB
  // refetch window that could pair the wrong assistant answer with a prior user.
  const allExchanges = useMemo(() => {
    return buildTranscriptExchanges({
      transcript,
      liveTimeline: session.timeline,
      sessionStatus: session.status,
      sessionConversationId: session.conversationId,
    });
  }, [session.conversationId, session.status, session.timeline, transcript]);

  // Un séparateur de compaction seul n'est pas « le dernier échange » : la barre
  // d'actions et les boutons d'agent restent sur la dernière vraie réponse.
  const lastConversationExchangeIndex = useMemo(
    () => findLastConversationExchangeIndex(allExchanges),
    [allExchanges],
  );

  const canOfferCompaction = canOfferConversationCompaction({
    accessState: composerAccessState,
    conversationId,
    hasHistory: allExchanges.length > 0,
    capability: session.compactContextCapability,
  });

  const lastUserMessage = useMemo(
    () => [...allExchanges].reverse().find((exchange) => exchange.userMessage)?.userMessage ?? null,
    [allExchanges],
  );

  const isHydratingTranscript = !!conversationId && isHistoryReady && !transcript;
  const isConversationEmpty = allExchanges.length === 0 && !isLoadingHistory && !isHydratingTranscript && !session.isStreaming;
  // Écran d'accueil PLEINE PAGE (logo, sélecteur de dossier, QuickActionsPanel) :
  // réservé à la page `/assistant`. Le panneau embarqué garde son propre accueil
  // et ses quick-actions hors sujet, mais peut toujours rendre le sélecteur normal
  // lorsqu'un hôte le déclare explicitement sélectionnable.
  const showEmptyState = !embedded && isConversationEmpty;
  // Écran d'accueil EMBARQUÉ : uniquement quand l'hôte le demande, et tant que
  // la conversation est vide. Les quick actions choisissent une variante
  // compacte, mais conservent la même garde : ni raccourci ni sélecteur de
  // source ne doit réapparaître au-dessus d'une timeline active.
  const showEmbeddedWelcome = embedded && !!embeddedEmptyState && isConversationEmpty;
  const embeddedQuickActions = embeddedEmptyState?.quickActions ?? [];
  const showEmbeddedQuickActions = showEmbeddedWelcome && embeddedQuickActions.length > 0;
  const showEmbeddedDecorativeWelcome = showEmbeddedWelcome && !showEmbeddedQuickActions;
  const isEmptyLoading = (isLoadingHistory || isHydratingTranscript) && allExchanges.length === 0;

  // Liste des tâches qui tourne sous le titre : générique sur `/assistant`,
  // propre à l'hôte (numéroter, renommer…) dans l'écran embarqué.
  const hostRotatingTasks = embeddedEmptyState?.rotatingTasks;
  const rotatingTasks = showEmbeddedDecorativeWelcome && hostRotatingTasks?.length
    ? hostRotatingTasks
    : SUGGESTED_TASKS;

  useEffect(() => {
    if (!showEmptyState && !showEmbeddedDecorativeWelcome) return;
    // Repart de 0 quand on (dé)monte un écran d'accueil : évite un index hors
    // borne si les deux listes n'ont pas la même longueur.
    setSuggestedTaskIndex(0);
    const id = setInterval(() => {
      setSuggestedTaskIndex((i) => (i + 1) % rotatingTasks.length);
    }, 2800);
    return () => clearInterval(id);
  }, [showEmptyState, showEmbeddedDecorativeWelcome, rotatingTasks.length]);

  // Notify parent of streaming state changes + scroll once when agent starts responding
  useEffect(() => {
    if (session.isStreaming) {
      onStreamingStart?.();
      requestAnimationFrame(() => bottomSentinelRef.current?.scrollIntoView({ block: 'end' }));
    } else {
      onStreamingEnd?.();
    }
  }, [session.isStreaming, onStreamingStart, onStreamingEnd]);

  /**
   * `tools_config` du prochain échange de ce composer. Partagé par l'envoi et la
   * compaction manuelle : le backend reconstruit la conversation à partir de
   * cette configuration pour recompter la mémoire, qui doit donc être celle du
   * tour suivant et pas une variante.
   */
  const buildComposerToolsConfig = useCallback(() => mapToolOptionsToWS(
    DEFAULT_TOOL_OPTIONS,
    caseContextMode,
  ), [caseContextMode]);

  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const scrollIntentRef = useRef<ChatScrollIntent>(createChatScrollIntent());
  const pendingUserAnchorExchangeIdRef = useRef<string | null>(null);
  const exchangeAnchorRefs = useRef(new Map<string, HTMLDivElement>());
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const setScrollIntent = useCallback((next: ChatScrollIntent) => {
    scrollIntentRef.current = next;
    setShowScrollToBottom(next.showScrollToBottom);
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior });
      return;
    }
    bottomSentinelRef.current?.scrollIntoView({ block: 'end', behavior });
  }, []);

  const resumeBottomFollow = useCallback((behavior: ScrollBehavior = 'auto') => {
    setScrollIntent(requestBottomFollow(scrollIntentRef.current));
    requestAnimationFrame(() => scrollToBottom(behavior));
  }, [scrollToBottom, setScrollIntent]);

  const registerExchangeAnchor = useCallback((exchangeId: string, node: HTMLDivElement | null) => {
    if (node) {
      exchangeAnchorRefs.current.set(exchangeId, node);
      return;
    }
    exchangeAnchorRefs.current.delete(exchangeId);
  }, []);

  const scrollToExchangeAnchor = useCallback((exchangeId: string) => {
    const node = exchangeAnchorRefs.current.get(exchangeId);
    if (!node) return false;
    node.scrollIntoView({ block: 'end' });
    return true;
  }, []);

  // Detect EVERY viewport movement, including scrollbar drag and inertial trackpad
  // scroll. The previous wheel/touch-only detection missed some real user
  // scrolls, so the ResizeObserver could keep pinning the bottom and make the
  // chat visibly fight the lawyer's scroll.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const handleScroll = () => {
      setScrollIntent(applyViewportScroll(scrollIntentRef.current, readScrollMetrics(el)));
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [setScrollIntent]);

  // Suivi du bas : actif pendant le streaming, ET maintenu ~1,2 s après la fin pour absorber
  // le reflow du commit local agent_done (timeline live → exchange historique local) — sinon
  // l'utilisateur se retrouve « remonté » au message précédent quand l'agent a fini d'écrire.
  useEffect(() => {
    if (session.isStreaming) {
      if (!scrollIntentRef.current.isUserDetached) {
        setScrollIntent(requestBottomFollow(scrollIntentRef.current));
      }
      requestAnimationFrame(() => {
        if (shouldFollowContentGrowth(scrollIntentRef.current)) scrollToBottom();
      });
      return undefined;
    }

    // Fin du streaming : on garde une courte fenêtre de follow seulement si
    // l'utilisateur est resté au live. S'il s'est détaché, le bouton reste visible
    // et aucun reflow markdown/tool-result ne reprend la main.
    if (!shouldFollowContentGrowth(scrollIntentRef.current)) {
      setScrollIntent(stopBottomFollow(scrollIntentRef.current));
      return undefined;
    }

    const graceId = setTimeout(() => {
      setScrollIntent(stopBottomFollow(scrollIntentRef.current));
    }, 1200);
    return () => clearTimeout(graceId);
  }, [scrollToBottom, session.isStreaming, setScrollIntent]);

  // Auto-suivi du bas pendant le streaming : tant que l'agent écrit ET que l'utilisateur
  // est resté au live, on ré-épingle la vue au bas à chaque fois que le contenu
  // grandit (nouveau texte). S'il remonte via molette, trackpad, touch ou scrollbar,
  // le contrôleur passe en mode détaché et on ne force plus jamais le scroll.
  // Callback ref : l'observer se (re)branche proprement quand le div de contenu monte/démonte
  // (il n'existe pas pendant l'état de chargement).
  const streamFollowObserverRef = useRef<ResizeObserver | null>(null);
  const messagesContentRef = useCallback((node: HTMLDivElement | null) => {
    streamFollowObserverRef.current?.disconnect();
    streamFollowObserverRef.current = null;
    if (!node) return;
    const ro = new ResizeObserver(() => {
      if (shouldFollowContentGrowth(scrollIntentRef.current)) {
        scrollToBottom();
      }
    });
    ro.observe(node);
    streamFollowObserverRef.current = ro;
  }, [scrollToBottom]);

  useEffect(() => {
    const exchangeId = pendingUserAnchorExchangeIdRef.current;
    if (!exchangeId) return;
    if (!allExchanges.some((exchange) => exchange.id === exchangeId)) return;

    requestAnimationFrame(() => {
      if (scrollToExchangeAnchor(exchangeId)) {
        pendingUserAnchorExchangeIdRef.current = null;
      }
    });
  }, [allExchanges, scrollToExchangeAnchor]);

  // À l'ouverture d'une conversation existante : positionner la vue sur le DERNIER
  // message (bas), comme une messagerie — sinon on atterrissait en haut de l'historique.
  // Une seule fois par conversation (ref de garde) pour ne pas réaccrocher le scroll à
  // chaque re-render de transcript ni combattre un scroll manuel de l'utilisateur.
  //
  // Un simple scroll après 1-2 rAF ne suffit PAS : le contenu (markdown, tables,
  // visualizers) se peint de façon asynchrone APRÈS le 1er paint, donc la hauteur
  // grandit ensuite et repousse le bas. On ré-épingle donc le bas via un
  // ResizeObserver sur le conteneur, pendant une courte fenêtre de stabilisation.
  // Le hook d'historique peut rendre un cache précédent avant que la clé active
  // soit observée. Les messages portent leur `conversationId` → on ne scrolle que
  // quand les données chargées appartiennent réellement à la conv active.
  const loadedConversationId = dbMessages[0]?.conversationId ?? null;
  const openScrolledConvRef = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationId || isLoadingHistory || historicalExchanges.length === 0) return undefined;
    if (loadedConversationId !== conversationId) return undefined; // données encore périmées
    if (openScrolledConvRef.current === conversationId) return undefined;

    const el = scrollContainerRef.current;
    if (!el) return undefined; // conteneur pas encore monté → on réessaiera au prochain render
    // Ne « consommer » la garde qu'une fois le conteneur disponible (sinon la conv ne
    // scrollerait jamais en bas si el était null au 1er passage).
    openScrolledConvRef.current = conversationId;

    let cancelled = false;
    // Scroll DIRECT sur le conteneur (`scrollTop = scrollHeight`) plutôt que
    // `scrollIntoView` sur le sentinel : plus fiable selon le timing/layout (scrollIntoView
    // pouvait viser le mauvais ancêtre et laisser la vue en haut). Double rAF pour laisser
    // le 1er paint se faire, puis ré-épingle à chaque reflow (contenu lourd peint en
    // plusieurs étapes) pendant 1,5 s — sauf si l'utilisateur scrolle vers le haut.
    const pin = () => {
      if (!cancelled && shouldFollowContentGrowth(scrollIntentRef.current)) {
        el.scrollTop = el.scrollHeight;
      }
    };
    setScrollIntent(requestBottomFollow(scrollIntentRef.current));
    pin();
    requestAnimationFrame(() => requestAnimationFrame(pin));
    const ro = new ResizeObserver(pin);
    ro.observe(el.firstElementChild ?? el);
    const stop = setTimeout(() => {
      cancelled = true;
      ro.disconnect();
      setScrollIntent(stopBottomFollow(scrollIntentRef.current));
    }, 1500);
    return () => { cancelled = true; ro.disconnect(); clearTimeout(stop); };
  }, [conversationId, isLoadingHistory, loadedConversationId, historicalExchanges.length, setScrollIntent]);

  // Le scroll en bas est piloté par : l'auto-suivi pendant le streaming (ResizeObserver
  // sur messagesContentRef + chatScrollBehavior), l'effet d'ouverture ci-dessus,
  // l'ancre du nouveau message user, et le bouton explicite de retour au live.

  const handleUserPromptClick = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
  }, []);

  const handleEmbeddedQuickActionClick = useCallback((action: EmbeddedQuickAction) => {
    setSelectedEmbeddedQuickActionId(action.id);
    setPendingPrompt(action.prompt);
  }, []);

  const handlePromptConsumed = useCallback(() => {
    setPendingPrompt(null);
  }, []);

  // Pièces jointes posées sur le composeur monté, quel qu'il soit (accueil, fil,
  // accueil embarqué) : celles du brouillon de l'hôte, puis les fichiers qu'un
  // message de CETTE conversation n'a pas pu emporter.
  const piecesJointesPosees = usePiecesJointesPoseesSurLeComposeur(conversationId, composerDraft);

  /**
   * Zone scrollable de l'accueil embarqué (tout ce qui précède le composer).
   *
   * Le composer est ancré en bas et grandit vers le haut : poser un brouillon
   * long réduit d'autant cette zone. On la recale alors au bas pour garder sous
   * les yeux ce qui compte — le sélecteur de source et sa charnière — plutôt que
   * le logo, seul élément vraiment sacrifiable. `requestAnimationFrame` : la
   * textarea se redimensionne dans son propre effet, on mesure après elle.
   */
  const embeddedWelcomeScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = embeddedWelcomeScrollRef.current;
    if (!el) return;
    const pinToBottom = () => { el.scrollTop = el.scrollHeight; };
    const frame = requestAnimationFrame(pinToBottom);
    // Le zoom navigateur, un redimensionnement du panneau ou la croissance du
    // composer réduisent cette zone SANS changer le brouillon. Sans observateur,
    // la vue resterait figée en haut (sur le logo) et le sélecteur de source
    // passerait sous le pli — constaté à 150 % de zoom.
    const observer = new ResizeObserver(pinToBottom);
    observer.observe(el);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [pendingPrompt, showEmbeddedWelcome]);

  /**
   * Brouillon de l'hôte → composer. **Aucun envoi** : l'avocat relit et appuie
   * lui-même sur envoyer (cf. `composerDraft`).
   *
   * On passe par le même `pendingPrompt` que les quick actions ; les pièces
   * jointes ont leur propre canal (`usePiecesJointesPoseesSurLeComposeur`) parce
   * qu'elles vivent dans l'état interne du composer. Les deux réagissent au même
   * `composerDraft`, donc au même rendu : un brouillon ne peut jamais arriver à
   * moitié (texte d'un acte, fichier d'un autre).
   */
  useEffect(() => {
    if (!composerDraft) return;
    setPendingPrompt(composerDraft.content);
    if (composerDraft.content === '') setSelectedEmbeddedQuickActionId(null);
    onComposerDraftConsumed?.();
  }, [composerDraft, onComposerDraftConsumed]);

  useEffect(() => {
    setSelectedEmbeddedQuickActionId(embeddedEmptyState?.initialQuickActionId ?? null);
  }, [embeddedEmptyState?.initialQuickActionId]);

  /**
   * Open + register a pool session for `targetConversationId` and attach it to
   * this composer. Single opening path for sending AND manual compaction: a
   * reopened conversation has no socket until one of them needs it.
   *
   * The new-conversation side effects (sidebar authorship, URL write) are gated
   * on `isNewConversation`: an existing conversation already has a DB row.
   *
   * @returns the verified key, or `null` after telling the lawyer why (open
   * failure or pool saturation) — `onAbandon` then runs first so the caller can
   * roll back its optimistic UI.
   */
  const openConversationSession = async ({
    targetConversationId,
    caseId,
    userId,
    isNewConversation,
    isForcedNewConversation,
    onAbandon,
  }: {
    targetConversationId: string;
    caseId: string | null;
    userId: string;
    isNewConversation: boolean;
    isForcedNewConversation: boolean;
    onAbandon: () => void;
  }): Promise<string | null> => {
    let newKey: string | null;
    try {
      newKey = await pool.openSession(
        userId,
        targetConversationId,
        caseId ?? undefined,
        caseContextMode,
      );
    } catch (error) {
      onAbandon();
      ErrorService.handle(error, {
        silent: true,
        context: { action: 'open_conversation_session', conversation_id: targetConversationId },
      });
      toast.error('Impossible d’ouvrir la conversation. Vous pouvez réessayer.');
      return null;
    }
    if (!newKey) {
      // Pool saturated (MAX_ACTIVE_SLOTS) — no agent response will follow.
      // Single saturation exit shared by every call site.
      onAbandon();
      toast.error(`Vous avez déjà ${MAX_ACTIVE_SLOTS} conversations en cours. Attendez qu'une se termine.`);
      return null;
    }
    const matchingNewKey = resolveMatchingLiveSessionKey({
      sessionKey: newKey,
      targetConversationId,
      getSessionStatus: (key) => pool.getSessionStatus(key),
      getConversationId: (key) => pool.getConversationId(key),
    });
    if (!matchingNewKey) {
      // openSession is the source of the pool mapping. If it ever returns a
      // different or already-dead slot, registering that key would corrupt the
      // transcript owner before the final send guard gets a chance to reject it.
      onAbandon();
      ErrorService.handle(new Error(
        `WebSocket session identity validation failed after open: targetConversationId=${targetConversationId}, sessionKey=${newKey}, mappedConversationId=${pool.getConversationId(newKey) ?? 'null'}, status=${pool.getSessionStatus(newKey) ?? 'null'}. Abort registration and open a fresh matching session before retrying.`,
      ), {
        silent: true,
        context: {
          action: 'validate_opened_ws_session_identity',
          conversation_id: targetConversationId,
          sessionKey: newKey,
        },
      });
      return null;
    }
    useActiveSessionsStore.getState().createSession(matchingNewKey, targetConversationId, caseId);
    // The sidebar phantom was created synchronously before the handshake;
    // only its route ownership waits for a live session.
    if (isNewConversation) {
      useActiveSessionsStore.getState().markConversationAuthored(targetConversationId);
    }

    const routeActivation = resolveRouteActivation(targetConversationId, isNewConversation);
    // The forced blank-route gesture has already written B into the URL. Its
    // mounted parent may still hold A for this render, so bypass that stale
    // route observation and attach the exact B key directly.
    if (isForcedNewConversation) {
      onSessionKeyChange(matchingNewKey);
    }
    if (routeActivation !== 'background') {
      onSessionKeyChange(matchingNewKey);
    }
    // Empty assistant page → first message: write ?c=<id> only while this
    // phantom still owns the empty route. A later navigation must leave the
    // newly opened target registered in the background without rewiring B.
    if (routeActivation === 'new_target_owns_empty_route') {
      handleConversationCreated(targetConversationId);
    }
    return matchingNewKey;
  };

  /** Returns whether the turn reached the WebSocket, so host-originated text is never lost. */
  const handleSendMessage = async (
    message: string,
    files?: File[],
    forcedNewConversationId?: string,
  ): Promise<boolean> => {
    const hasMeaningfulMessage = hasMeaningfulChatContent(message);
    if (!hasMeaningfulMessage && !files?.length) return false;
    // A forced id is created from the browser's blank route. The props still
    // belong to A for this event turn, so A's stream/transcript must not reject
    // B before B has its own session.
    // `isCompactingContext` : même verrou backend que ci-dessus (`conversation_busy`).
    if (!forcedNewConversationId && (session.isStreaming || hasActiveTranscriptTurn || isCompactingContext)) return false;
    if (!user) {
      ErrorService.handle(new Error("Utilisateur non authentifié"), { userMessage: "La réponse a été interrompue. Vous pouvez relancer votre demande.", context: { action: 'send_message' } });
      return false;
    }

    // Keep the browser's visible contract aligned with the server's semantic
    // guard: invisible-only input is attachment context, not an instruction.
    const userMessage = hasMeaningfulMessage ? message.trim() : '';
    const userId = user.id;
    // The selector affects the next turn only. Snapshot before any async session
    // opening/upload work so a later selector change cannot retarget this exchange.
    const turnCaseId = selectedCaseId;
    // Même instantané pour les gestes sur la liste. Une nouvelle conversation forcée
    // n'emporte pas ceux du fil encore affiché pendant ce rendu.
    const {
      cleDesGestes: turnCaseChangesKey,
      gestes: turnCaseChanges,
      principalServeur: turnPrincipalCaseId,
    } = conversationCases.lireTour(forcedNewConversationId !== undefined);
    const turnUiActionCapabilities = [...uiActionCapabilities];
    // A global “Nouvelle conversation” click can clear the browser route just
    // before this persistent composer receives its next React props. Capturing
    // the UUID at that gesture is the only safe way to guarantee that its first
    // message starts B rather than mutating the still-rendered A.
    const isNewConversation = forcedNewConversationId !== undefined || !conversationId;
    const targetConvId = forcedNewConversationId ?? conversationId ?? crypto.randomUUID();
    const currentComposerAccessSnapshot = forcedNewConversationId
      ? { conversationId: null, accessState: 'editable' as const }
      : composerAccessSnapshotRef.current;

    // The callback may survive one render after navigation. Only start an
    // existing-conversation send when the current route still names its target;
    // the captured target snapshot below then remains valid across later awaits.
    if (!isNewConversation && currentComposerAccessSnapshot.conversationId !== targetConvId) return false;
    if (!canUseConversationComposerActions(currentComposerAccessSnapshot.accessState)) return false;

    const sendLockIdentity = getConversationSendLockIdentity({
      targetConversationId: targetConvId,
      isNewConversation,
      locationKey: location.key,
    });
    const sendLock = acquireConversationSendLock(
      pendingSendLocksRef.current,
      sendLockIdentity,
      targetConvId,
    );
    if (!sendLock.acquired) return false;
    replacePendingSendLocks(sendLock.locks);

    const capturedComposerAccessSnapshot: ConversationComposerAccessSnapshot = {
      conversationId: targetConvId,
      accessState: currentComposerAccessSnapshot.accessState,
    };

    try {

    if (isNewConversation) {
      beginPendingConversation(targetConvId);
      // The sidebar lives outside this chat tree. Register the optimistic row
      // before the WebSocket handshake so navigation cannot observe a route
      // owner without its corresponding conversation identity.
      useActiveSessionsStore.getState().addPendingConversation(targetConvId, turnCaseId);
    }
    const localTurnId = crypto.randomUUID();

    // Active transcript turn FIRST — instant feedback, BEFORE
    // the WS handshake (`await pool.openSession()`) and the document upload. Without
    // this, every bit of feedback waits behind the connect, leaving the welcome
    // screen up "as if nothing happened" — most jarring when attaching documents.
    // The same localTurnId survives pending → streaming → committed, so React
    // updates one subtree instead of pairing the final timeline with another prompt.
    const userMessageObj: ChatMessage = {
      id: crypto.randomUUID(),
      conversationId: targetConvId,
      role: 'user',
      content: userMessage,
      createdAt: new Date(),
      isStreaming: false,
      streamingComplete: true,
      ...(files?.length ? { metadata: { attachedFilenames: files.map(f => f.name) } } : {}),
    };
    if (!beginTurn(
      targetConvId,
      localTurnId,
      userMessageObj,
      turnCaseId,
      turnUiActionCapabilities,
    )) {
      if (isNewConversation) {
        clearPendingConversation();
        useActiveSessionsStore.getState().removePendingConversation(targetConvId);
      }
      toast.error('Une réponse est déjà en cours pour cette conversation.');
      return false;
    }
    pendingUserAnchorExchangeIdRef.current = `turn:${localTurnId}`;
    setScrollIntent(requestBottomFollow(scrollIntentRef.current));
    if (files?.length) {
      replacePendingSendLocks(setConversationSendUploadingFiles(
        pendingSendLocksRef.current,
        sendLockIdentity,
        files.map((file) => file.name),
      ));
    }

    // Ensure a LIVE pool session backs this conversation before sending.
    //
    // WHY re-open on a dead key (not just a null key): the pool tears sessions
    // down on backend inactivity expiry (30 min), eviction, or manual close.
    // After that `sessionKey` may still be non-null (a store/React ref the bridge
    // hasn't cleared yet, or a session that died while being viewed) but
    // `pool.getSessionStatus` returns null. Sending on it would hit
    // `sendMessage: session not found` and be silently dropped. We re-open
    // instead, reusing the conversationId (history preserved). (2026-06-26)
    //
    // 1 WS = 1 conversation: a brand-new conversation generates its UUID here; an
    // existing one reuses the URL-derived `conversationId` (SSOT).
    const rollbackLocalTurn = () => {
      // No session/agent response will follow — drop the optimistic bubble + chips.
      rollbackTurn(targetConvId, localTurnId);
      if (isNewConversation) {
        clearPendingConversation();
        useActiveSessionsStore.getState().removePendingConversation(targetConvId);
        // L'URL nomme déjà ce fil (écrit au geste d'envoi), alors qu'il n'existe
        // pas en base. Gardé, il survit au rafraîchissement : la paternité locale
        // est perdue, le détail répond 404, et l'avocat reste devant « Impossible
        // de confirmer vos droits » sans issue (#2378, 2026-10-21).
        // Un fil déjà confirmé (`conversation_created` arrive avant la
        // persistance du message) existe en base : l'avocat y reste.
        if (!useActiveSessionsStore.getState().confirmedConversations[targetConvId]) {
          onConversationAbandoned?.(targetConvId, { content: message, files: files ?? [] });
        }
      }
    };

    const openSessionForSend = (): Promise<string | null> => openConversationSession({
      targetConversationId: targetConvId,
      caseId: turnCaseId,
      userId,
      isNewConversation,
      isForcedNewConversation: forcedNewConversationId !== undefined,
      onAbandon: rollbackLocalTurn,
    });

    let currentSessionKey = resolveMatchingLiveSessionKey({
      sessionKey,
      targetConversationId: targetConvId,
      getSessionStatus: (key) => pool.getSessionStatus(key),
      getConversationId: (key) => pool.getConversationId(key),
    });
    // Keep `!currentSessionKey` as a direct disjunct so TS narrows it to a string
    // after the block. Assigning currentSessionKey in this (outer) scope — not via
    // a closure — is what preserves that narrowing down to pool.sendMessage.
    if (!currentSessionKey) {
      currentSessionKey = await openSessionForSend();
      if (!currentSessionKey) return false; // saturated — openSessionForSend already rolled back
    }

    // Téléversement des pièces jointes — le backend prend un fichier par requête.
    // Le sablier de chaque vignette a été posé plus haut (avant la connexion) ;
    // ici il tombe fichier par fichier.
    //
    // Deux choses ont changé avec le plafond de 50 documents (#2066) :
    //  - trois envois de front au lieu d'une série (voir `uploadTurnAttachments`) ;
    //  - un fichier en échec n'annule plus le message. Auparavant le premier
    //    refus faisait perdre le tour entier et l'avocat recommençait tout. Le
    //    message part avec les fichiers téléversés ; un toast dit ce qu'il est
    //    advenu des autres, une fois le message accepté (plus bas). L'agent n'en
    //    est pas informé : c'est l'avocat qui décide de la suite.
    let attachedDocuments: AttachedDocument[] | undefined;
    let unsentAttachments: UnsentAttachment[] = [];
    let filesToReturn: File[] = [];
    let sentFileCount = 0;
    if (files && files.length > 0) {
      // Suivi par FICHIER et non par nom : deux pièces peuvent porter le même nom.
      const stillUploading = new Set(files);
      try {
        const { uploaded, sentFiles, failed } = await uploadTurnAttachments(
          files,
          async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            // Rattache la pièce à son fil dès le dépôt : c'est ce lien qui permet
            // à la suppression d'une conversation d'emporter ses pièces jointes.
            // `targetConvId` est l'UUID que ce front génère lui-même (INV-3), donc
            // connu avant même que la conversation n'existe côté serveur.
            if (targetConvId) formData.append('conversation_id', targetConvId);
            const result = await apiClient.post<{
              temp_file_id: string;
              public_url: string;
              original_filename: string;
            }>('/case-discovery-v3/upload', formData);
            return {
              source: 'uploaded' as const,
              temp_file_id: result.temp_file_id,
              original_filename: result.original_filename,
              public_url: result.public_url,
            };
          },
          {
            onFileSettled: (file) => {
              stillUploading.delete(file);
              replacePendingSendLocks(setConversationSendUploadingFiles(
                pendingSendLocksRef.current,
                sendLockIdentity,
                [...stillUploading].map((pending) => pending.name),
              ));
            },
          },
        );

        if (uploaded.length === 0) {
          // Aucun fichier n'est parti : la question portait sur des pièces qui
          // ne sont pas là. Le tour est annulé et le brouillon rendu en entier,
          // comme avant — même s'il y avait du texte.
          rollbackLocalTurn();
          const [first, ...others] = failed;
          // Un fichier que le serveur refuse pour de bon se dit avec son nom et
          // sa cause : sans cela l'avocat renverrait le même message, qui
          // échouerait de la même façon.
          const refusal = describeRefusedAttachments(failed.map(({ file, cause }) => ({ name: file.name, cause })));
          ErrorService.handle(first?.error, {
            ...(refusal ? { userMessage: refusal } : {}),
            context: { action: 'upload_agent_files' },
          });
          others.forEach(({ error }) => ErrorService.handle(error, {
            silent: true,
            context: { action: 'upload_agent_files', conversation_id: targetConvId },
          }));
          return false;
        }

        attachedDocuments = uploaded;
        sentFileCount = sentFiles.length;
        if (failed.length > 0) {
          unsentAttachments = failed.map(({ file, cause }) => ({ name: file.name, cause }));
          // Seul un échec passager vaut un nouvel essai. Un fichier que le
          // serveur refusera toujours (format, poids) ne revient pas : le
          // rendre inviterait à un envoi qui échouerait de nouveau.
          filesToReturn = failed.filter(({ cause }) => cause === 'retryable').map(({ file }) => file);
          failed.forEach(({ error }) => ErrorService.handle(error, {
            silent: true,
            context: { action: 'upload_agent_files', conversation_id: targetConvId },
          }));
          // La bulle a été créée avant le téléversement avec tous les noms :
          // elle ne doit plus annoncer que ce qui part réellement.
          replaceActiveTurnUserMessage(targetConvId, localTurnId, {
            ...userMessageObj,
            metadata: { ...userMessageObj.metadata, attachedFilenames: sentFiles.map((file) => file.name) },
          });
        }
      } finally {
        replacePendingSendLocks(setConversationSendUploadingFiles(
          pendingSendLocksRef.current,
          sendLockIdentity,
          [],
        ));
      }
    }

    // Build tools config and send via WebSocket
    const toolsConfig = buildComposerToolsConfig();

    // Re-validate liveness AFTER the upload await: a session live at the check
    // above can be torn down during the upload (eviction, backend expiry), and
    // pool.sendMessage silently drops a message on a missing session. Re-open
    // before sending so the message is never lost — closes the time-of-check /
    // time-of-use gap between the pre-check and this send. (2026-06-26)
    currentSessionKey = resolveMatchingLiveSessionKey({
      sessionKey: currentSessionKey,
      targetConversationId: targetConvId,
      getSessionStatus: (key) => pool.getSessionStatus(key),
      getConversationId: (key) => pool.getConversationId(key),
    });
    if (!currentSessionKey) {
      currentSessionKey = await openSessionForSend();
      if (!currentSessionKey) return false; // saturated — openSessionForSend already rolled back
    }

    // An existing conversation can be demoted while session creation or an upload
    // is in flight. A same-target route update is authoritative, but navigation
    // must fall back to the captured target access rather than borrowing the
    // destination conversation's role. A new frontend phantom remains exempt:
    // its first URL transition temporarily resolves access as loading before the
    // backend-created detail exists.
    if (!canSendToConversationTarget({
      targetConversationId: targetConvId,
      isNewConversation,
      capturedSnapshot: capturedComposerAccessSnapshot,
      currentSnapshot: composerAccessSnapshotRef.current,
    })) {
      rollbackLocalTurn();
      return false;
    }

    // No await may separate this identity check from the mutation boundary. The
    // pool key can outlive its React owner during route switches, so neither the
    // transcript nor the wire send may ever be bound by key alone.
    const finalSessionKey = resolveMatchingLiveSessionKey({
      sessionKey: currentSessionKey,
      targetConversationId: targetConvId,
      getSessionStatus: (key) => pool.getSessionStatus(key),
      getConversationId: (key) => pool.getConversationId(key),
    });
    if (!finalSessionKey) {
      rollbackLocalTurn();
      return false;
    }
    bindOwnerSession(targetConvId, finalSessionKey);
    let caseScopeRefusal = null as ConversationCaseRefusal | null;
    const stopWatchingCaseScopeRefusal = pool.events.on('case_scope_refused', (eventSessionKey, refusal) => {
      if (eventSessionKey === finalSessionKey) caseScopeRefusal = refusal;
    });
    conversationCases.marquerGestesEnVol(turnCaseChangesKey, turnCaseChanges);
    try {
      const acceptedByBackend = await pool.sendMessageAndWaitForAcceptance(finalSessionKey, userMessage, toolsConfig, {
        attachments: attachedDocuments,
        localTurnId,
        caseId: turnCaseId,
        caseChanges: turnCaseChanges,
        caseIsAlreadyPrincipal: turnCaseId !== null && turnCaseId === turnPrincipalCaseId,
        // One host-owned capability list serves both protocol advertisement and
        // the final render defense; clone it so this turn keeps an immutable snapshot.
        uiActionCapabilities: turnUiActionCapabilities,
      });
      if (!acceptedByBackend) {
        rollbackLocalTurn();
        if (caseScopeRefusal) {
          // Un premier message refusé rend la page vierge : la frise qui portait le
          // refus disparaît avec lui, et l'avocat ne saurait pas pourquoi.
          if (isNewConversation) toast.error(caseScopeRefusal.message, { duration: 15_000 });
          void conversationCases.reprendreApresRefus({
            cleDesGestes: turnCaseChangesKey,
            refus: caseScopeRefusal,
            conversationCible: isNewConversation ? null : targetConvId,
            estEncoreAffichee: () => sessionConversationIdRef.current === targetConvId,
          });
        }
        return false;
      }
      conversationCases.consommerGestes(turnCaseChangesKey, turnCaseChanges);
    } catch (error) {
      rollbackLocalTurn();
      toast.error('Une réponse est déjà en cours pour cette conversation.');
      ErrorService.handle(error, {
        silent: true,
        context: { action: 'send_message_while_exchange_pending', conversation_id: targetConvId },
      });
      return false;
    } finally {
      stopWatchingCaseScopeRefusal();
      conversationCases.terminerGestesEnVol(turnCaseChangesKey);
    }

    trackEvent('case_discovery_message_sent', {
      feature: 'case_discovery_v3',
      has_case: !!turnCaseId,
      tools_enabled: {
        jurisprudence: toolsConfig.search_jurisprudence,
        doctrine: toolsConfig.search_doctrine,
        legifrance: toolsConfig.legifrance_search_code,
      },
      has_attachments: (attachedDocuments?.length ?? 0) > 0,
    });
    if (unsentAttachments.length > 0) {
      // Dit seulement maintenant, une fois le message accepté : annoncé plus
      // tôt, « les autres documents sont bien partis » serait faux si l'envoi
      // était ensuite refusé (le brouillon revient alors en entier).
      //
      // Les fichiers à renvoyer passent par le relais de LEUR conversation, pas
      // par le composeur qui a lancé cet envoi : au premier message il a été
      // remplacé par celui du fil, et l'avocat a pu changer de conversation
      // pendant le téléversement. Le toast dit où ils sont — sous ses yeux
      // seulement s'il regarde encore cette conversation.
      rendreFichiersAuComposeur(targetConvId, filesToReturn);
      toast.error(
        describeUnsentAttachments(unsentAttachments, sentFileCount, {
          composerVisible: lireConversationAffichee() === targetConvId,
        }),
        { ...toastDuration(15_000) },
      );
    }
    return true;

    } finally {
      replacePendingSendLocks(releaseConversationSendLock(
        pendingSendLocksRef.current,
        sendLockIdentity,
      ));
    }
  };

  // A host handoff must be consumed exactly once: React StrictMode can replay
  // the effect and the component stays mounted across conversations. When a
  // turn cannot start, return the text to the visible composer instead.
  const handleSendMessageRef = useRef(handleSendMessage);
  handleSendMessageRef.current = handleSendMessage;

  /**
   * Routes a physical composer gesture to the handler for the route visible at
   * the time of the gesture.
   *
   * React Router updates `window.location` synchronously, but an input that
   * survives the navigation can still hold the previous render's onSend for one
   * event turn. Calling that stale closure would correctly refuse A, yet lose
   * the lawyer's first message for the newly visible blank route B. Wait only
   * until React has committed that same route, then read the latest handler
   * ref; no background retry or queued conversation is created.
   */
  const handleComposerSend = useCallback(async (message: string, files?: File[]) => {
    const browserConversationId = new URLSearchParams(window.location.search).get('c');
    const routeTransition = resolveComposerRouteTransition({
      conversationRouteOwner,
      browserConversationId,
      conversationId,
    });
    if (routeTransition === 'start_route_owned_conversation') {
      const newConversationId = crypto.randomUUID();
      // Publish the phantom before changing the URL. Otherwise the B render
      // briefly treats its client-minted id as an unknown persisted thread and
      // disables the composer before this first send reaches the pool.
      useActiveSessionsStore.getState().addPendingConversation(newConversationId, selectedCaseId);
      navigate({ pathname: '/assistant', search: `?c=${newConversationId}` });
      return handleSendMessageRef.current(message, files, newConversationId);
    }
    if (routeTransition === 'await_route_owner') {
      const deadline = performance.now() + 500;
      while (sessionConversationIdRef.current !== browserConversationId && performance.now() < deadline) {
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }

      // The browser route is authoritative. If React has not caught up within
      // this bounded UI transition, never let A's stale closure mutate A again.
      if (sessionConversationIdRef.current !== browserConversationId) return false;
    }
    return handleSendMessageRef.current(message, files);
  }, [conversationId, conversationRouteOwner, navigate, selectedCaseId]);
  const appliedHostSubmissionNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hostSubmission || appliedHostSubmissionNonceRef.current === hostSubmission.nonce) return;
    appliedHostSubmissionNonceRef.current = hostSubmission.nonce;
    const { content } = hostSubmission;
    void handleSendMessageRef.current(content)
      .catch(() => false)
      .then((sent) => {
        if (!sent) setPendingPrompt(content);
      });
  }, [hostSubmission]);

  // « Réessayer » après une erreur de génération (item agent_error) : on relance le
  // DERNIER prompt utilisateur comme un nouveau tour. C'est le seul recours propre côté
  // protocole — un tour est piloté par un frame `user_message` (pas de « regenerate »
  // backend). La session WS reste vivante après une erreur LLM (≠ déconnexion), donc
  // `handleSendMessage` réutilise le sessionKey courant ; son guard `isStreaming` est
  // déjà retombé (status 'error'). Voir incident iveillard@alchimie 2026-06-12.
  const handleRetry = () => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return;
    const content = lastUserMessage?.content?.trim();
    if (content) void handleSendMessage(content);
  };

  const handleConnectionRetry = useCallback(() => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return;
    if (!matchingActiveSessionKey || getCurrentMatchingSessionKey() !== matchingActiveSessionKey) return;
    retryConnection();
  }, [getCurrentMatchingSessionKey, matchingActiveSessionKey, retryConnection]);

  const handleCancelStream = useCallback(() => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return;
    if (!matchingActiveSessionKey || getCurrentMatchingSessionKey() !== matchingActiveSessionKey) return;
    cancelStream();
  }, [cancelStream, getCurrentMatchingSessionKey, matchingActiveSessionKey]);

  /**
   * Envoi en vol pour ce fil, lu hors rendu : verrou d'envoi, tour du transcript,
   * statut du pool. `liveSessionKey` : la clé tout juste ouverte, que la prop
   * `sessionKey` ne porte qu'au rendu suivant.
   */
  const isConversationSendInFlight = (
    targetConversationId: string,
    liveSessionKey: string | null = getCurrentMatchingSessionKey(),
  ): boolean => {
    return hasConversationSendInFlight({
      sendLocks: pendingSendLocksRef.current,
      conversationId: targetConversationId,
      hasActiveTranscriptTurn:
        (useConversationTranscriptStore.getState().transcripts[targetConversationId]?.activeTurn ?? null) !== null,
      isSessionStreaming: liveSessionKey !== null && isExchangeActive(pool.getSessionStatus(liveSessionKey)),
    });
  };

  /**
   * Compaction manuelle depuis le rond du composer.
   *
   * Cas central : l'avocat ROUVRE une longue conversation qui dérive et veut la
   * compacter avant d'écrire. Aucune socket n'existe encore — elle ne s'ouvre
   * qu'au premier envoi —, donc le clic l'ouvre par le même chemin que l'envoi,
   * puis confie la demande au pool, qui attend l'ACK générique avant de décider.
   */
  const handleCompactContext = async (): Promise<void> => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return;
    const targetConversationId = sessionConversationIdRef.current;
    if (!targetConversationId || !user) return;
    if (compactionOpeningConversationIdRef.current === targetConversationId) return;
    // Lu à l'instant du clic (refs et stores), pas dans le dernier rendu : un
    // premier envoi peut ouvrir sa socket ou téléverser ses pièces sans que le
    // pool porte encore de `pendingMessage`. Accepter la compaction maintenant
    // ferait refuser cet envoi par le pool et retirerait le message de l'écran.
    if (isConversationSendInFlight(targetConversationId)) {
      toast.error(COMPACTION_BLOCKED_BY_SEND_MESSAGE);
      return;
    }
    // Le `tools_config` et le dossier du PROCHAIN message, figés au clic : le
    // backend reconstruit la conversation comme un tour pour recompter la mémoire.
    const caseId = selectedCaseId;
    const toolsConfig = buildComposerToolsConfig();

    let targetSessionKey = getCurrentMatchingSessionKey();
    if (!targetSessionKey) {
      compactionOpeningConversationIdRef.current = targetConversationId;
      setCompactionOpeningConversationId(targetConversationId);
      try {
        targetSessionKey = await openConversationSession({
          targetConversationId,
          caseId,
          userId: user.id,
          isNewConversation: false,
          isForcedNewConversation: false,
          // Rien d'optimiste à défaire : le rond redevient immobile au `finally`.
          onAbandon: () => undefined,
        });
      } finally {
        compactionOpeningConversationIdRef.current = null;
        setCompactionOpeningConversationId((current) => (current === targetConversationId ? null : current));
      }
      if (!targetSessionKey) return;
    }

    // Revalidé après l'ouverture : l'avocat a pu changer de fil, ou perdre le
    // droit d'écrire, pendant la poignée de main. La session reste alors ouverte
    // en arrière-plan, sans compaction qu'il n'a plus sous les yeux.
    if (sessionConversationIdRef.current !== targetConversationId) return;
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return;
    const finalSessionKey = resolveMatchingLiveSessionKey({
      sessionKey: targetSessionKey,
      targetConversationId,
      getSessionStatus: (key) => pool.getSessionStatus(key),
      getConversationId: (key) => pool.getConversationId(key),
    });
    if (!finalSessionKey) return;
    // Un envoi a pu démarrer pendant la poignée de main de cette compaction.
    if (isConversationSendInFlight(targetConversationId, finalSessionKey)) {
      toast.error(COMPACTION_BLOCKED_BY_SEND_MESSAGE);
      return;
    }
    if (!pool.compactContext(finalSessionKey, toolsConfig, { caseId })) {
      toast.error(COMPACTION_BLOCKED_BY_SEND_MESSAGE);
    }
  };

  // Timeline forms can remain mounted for one render across an access-state
  // transition. Check the ref directly before each WS send so an editor demoted
  // to viewer cannot submit a stale form callback after the UI becomes read-only.
  const handleJurisprudenceResponse = useCallback((
    ...args: Parameters<typeof sendJurisprudenceResponse>
  ): ReturnType<typeof sendJurisprudenceResponse> => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return;
    if (!matchingActiveSessionKey || getCurrentMatchingSessionKey() !== matchingActiveSessionKey) return;
    sendJurisprudenceResponse(...args);
  }, [getCurrentMatchingSessionKey, matchingActiveSessionKey, sendJurisprudenceResponse]);

  const handleJurisprudenceCancellation = useCallback((
    ...args: Parameters<typeof sendJurisprudenceCancellation>
  ): ReturnType<typeof sendJurisprudenceCancellation> => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return;
    if (!matchingActiveSessionKey || getCurrentMatchingSessionKey() !== matchingActiveSessionKey) return;
    sendJurisprudenceCancellation(...args);
  }, [getCurrentMatchingSessionKey, matchingActiveSessionKey, sendJurisprudenceCancellation]);

  const handleCaseWikiResetResponse = useCallback((
    ...args: Parameters<typeof sendCaseWikiResetResponse>
  ): ReturnType<typeof sendCaseWikiResetResponse> => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return false;
    if (!matchingActiveSessionKey || getCurrentMatchingSessionKey() !== matchingActiveSessionKey) return false;
    return sendCaseWikiResetResponse(...args);
  }, [getCurrentMatchingSessionKey, matchingActiveSessionKey, sendCaseWikiResetResponse]);

  const handleDeletePiecesConfirmationResponse = useCallback((
    ...args: Parameters<typeof sendDeletePiecesConfirmationResponse>
  ): ReturnType<typeof sendDeletePiecesConfirmationResponse> => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return false;
    if (!matchingActiveSessionKey || getCurrentMatchingSessionKey() !== matchingActiveSessionKey) return false;
    return sendDeletePiecesConfirmationResponse(...args);
  }, [getCurrentMatchingSessionKey, matchingActiveSessionKey, sendDeletePiecesConfirmationResponse]);

  const handleSkillChangeResponse = useCallback((
    ...args: Parameters<typeof sendSkillChangeResponse>
  ): ReturnType<typeof sendSkillChangeResponse> => {
    if (!canUseConversationComposerActions(composerAccessSnapshotRef.current.accessState)) return false;
    if (!matchingActiveSessionKey || getCurrentMatchingSessionKey() !== matchingActiveSessionKey) return false;
    return sendSkillChangeResponse(...args);
  }, [getCurrentMatchingSessionKey, matchingActiveSessionKey, sendSkillChangeResponse]);

  const embeddedEmptyComposer = (
    <>
      <ConnectionStatus
        hasSession={!!matchingActiveSessionKey}
        isConnected={session.isConnected}
        reconnectInfo={session.reconnectInfo}
        reconnectFailed={session.reconnectFailed}
        sessionStatus={session.status}
        onRetry={canUseComposerActions ? handleConnectionRetry : undefined}
      />
      <ComposerAccessSlot composerAccessState={composerAccessState} onRetry={onComposerAccessRetry} conversationCases={conversationCases.liste ?? []}>
        <HectorPromptInputBox
          className="!shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_20px_rgba(15,23,42,0.06)]"
          compactHeight={showEmbeddedQuickActions}
          // Accueil décoratif : le composer partage la hauteur avec le bloc de
          // l'hôte au-dessus. Un plafond en pixels fixes le laissait tout prendre
          // dès que le panneau raccourcissait (zoom 150 %) ; le plafond relatif
          // lui garde sa place sans effacer le sélecteur de source.
          textareaMaxHeight={showEmbeddedQuickActions ? undefined : 'min(240px, 30vh)'}
          onSend={handleComposerSend}
          onStop={handleCancelStream}
          isLoading={shouldBlockCurrentComposer}
          placeholder="Décrivez ce que vous voulez faire…"
          initialInput={pendingPrompt ?? undefined}
          onInitialInputConsumed={handlePromptConsumed}
          // Pièces du brouillon de l'hôte (le sélecteur de source d'un hôte
          // embarqué vit dans cet écran d'accueil) et fichiers rendus à la
          // conversation : mêmes props sur les trois composeurs.
          {...piecesJointesPosees}
          onDraftChange={onComposerDraftChange}
          showDocumentSelector={!!selectedCaseId}
          documentContext={documentContext}
          onDocumentContextChange={setDocumentContext}
          // Wizard « sans actions rapides » : pas de dropdown quick-actions.
          showQuickActionsDropdown={false}
          selectedCase={composerCase}
          availableCases={cases.map(c => ({ id: c.id, title: c.title }))}
          onCaseSelect={allowsHumanCaseSelection ? conversationCases.choisirDossier : undefined}
          {...composerRelatedCases}
          allowCaseClear={caseContextMode === 'selectable'}
        />
      </ComposerAccessSlot>
    </>
  );

  const restoreArchivedConversation = conversationId
    && conversationArchived
    && onConversationArchivedChange ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 shrink-0 gap-1.5 px-2.5 text-xs"
        aria-label="Désarchiver la conversation"
        title="Désarchiver la conversation"
        disabled={restoringConversationIds.has(conversationId)}
        onClick={() => {
          if (restoreRequestIdsRef.current.has(conversationId)) return;
          const requestId = restoreRequestSequenceRef.current + 1;
          restoreRequestSequenceRef.current = requestId;
          restoreRequestIdsRef.current.set(conversationId, requestId);
          setRestoringConversationIds((current) => new Set(current).add(conversationId));
          void onConversationArchivedChange(false)
            // The parent owns the actionable toast/log. The shared chat must not
            // leak a rejected click promise after it restores the button state.
            .catch(() => undefined)
            .finally(() => {
              if (restoreRequestIdsRef.current.get(conversationId) !== requestId) return;
              restoreRequestIdsRef.current.delete(conversationId);
              setRestoringConversationIds((current) => {
                const next = new Set(current);
                next.delete(conversationId);
                return next;
              });
            });
        }}
      >
        {restoringConversationIds.has(conversationId)
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          : <ArchiveRestore className="h-3.5 w-3.5" aria-hidden />}
        Désarchiver
      </Button>
    ) : null;

  return (
    <div className="dossier-chat-viewport flex flex-col h-full min-w-0 relative">
      {showEmptyState ? (
        /* Empty State - same as GlobalChatInterface */
        <div className="relative flex-1 flex flex-col items-center justify-center px-6 py-8">
          {restoreArchivedConversation && (
            <div className="absolute right-4 top-3 z-10">{restoreArchivedConversation}</div>
          )}
          <div className="mb-6">
            <img src="/logo_hector-black.png" alt="Hector" className="h-16 w-auto opacity-80" />
          </div>

          <div className="text-center max-w-xl mb-8">
            <h1 className="text-2xl font-semibold text-grey-900 mb-2">
              Comment puis-je vous aider ?
            </h1>
            <div className="text-grey-500 text-sm h-5 overflow-hidden" aria-live="polite">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={suggestedTaskIndex}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="inline-block"
                >
                  {SUGGESTED_TASKS[suggestedTaskIndex]}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>

          <div className="w-full max-w-2xl">
            {/* Case Selector */}
            {allowsHumanCaseSelection && (
              <div className="flex items-center justify-center mb-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-tour={selectedCaseId ? "assistant-case-selected" : "assistant-case-selector"} className={cn("h-8 px-3 text-xs border-grey-200 gap-1.5", selectedCaseId ? "text-charcoal" : "text-grey-500")}>
                      <FolderOpen className="w-3.5 h-3.5" />
                      {selectedCase ? <span className="truncate max-w-[200px]">{selectedCase.title}</span> : <span>Sélectionner un dossier</span>}
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-72">
                    <div className="p-2">
                      <Input placeholder="Rechercher un dossier..." value={caseSearchQuery} onChange={(e) => setCaseSearchQuery(e.target.value)} onKeyDown={(e) => e.stopPropagation()} className="h-8 text-xs" autoComplete="off" />
                    </div>
                    <DropdownMenuSeparator />
                    {caseContextMode === 'selectable' && (
                      <>
                        <DropdownMenuItem onClick={() => { conversationCases.choisirDossier(null); setCaseSearchQuery(""); }} className={cn("text-xs cursor-pointer", !selectedCaseId && "bg-grey-100")}>
                          <span className="text-grey-500">Aucun dossier (question générale)</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuLabel className="text-xs text-grey-400 font-normal">Vos dossiers</DropdownMenuLabel>
                    <div className="max-h-48 overflow-y-auto">
                      {isCasesLoading ? (
                        <div className="py-2 px-3 text-xs text-grey-500">Chargement...</div>
                      ) : filteredCases.length === 0 ? (
                        <div className="py-2 px-3 text-xs text-grey-500">Aucun dossier trouvé</div>
                      ) : (
                        filteredCases.map((caseItem) => (
                          <DropdownMenuItem key={caseItem.id} onClick={() => { conversationCases.choisirDossier(caseItem.id); setCaseSearchQuery(""); }} className={cn("text-xs cursor-pointer flex-col items-start", selectedCaseId === caseItem.id && "bg-grey-100")}>
                            <span className="font-medium truncate w-full">{caseItem.title}</span>
                            {caseItem.client_name && <span className="text-xs text-grey-500">{caseItem.client_name}</span>}
                          </DropdownMenuItem>
                        ))
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <ConnectionStatus
              hasSession={!!matchingActiveSessionKey}
              isConnected={session.isConnected}
              reconnectInfo={session.reconnectInfo}
              reconnectFailed={session.reconnectFailed}
              sessionStatus={session.status}
              onRetry={canUseComposerActions ? handleConnectionRetry : undefined}
            />
            <ComposerAccessSlot composerAccessState={composerAccessState} onRetry={onComposerAccessRetry} conversationCases={conversationCases.liste ?? []}>
            <div className="mb-2 flex justify-end">
              <CaseDiscoveryModelSelector />
            </div>
            <HectorPromptInputBox
              // Ombre fine « flottante » à deux couches (contact serré + ambiance douce,
              // teinte slate-900 rgba(15,23,42), opacités basses 0.05/0.06) : détache le
              // composer du fond blanc sans le « dark 4-sided blur » de l'ancien
              // shadow-[0_8px_30px_rgba(0,0,0,0.12)] (retiré car trop lourd). `!` requis :
              // le cn() local du composant est un simple join (pas tailwind-merge), donc
              // l'important garantit l'override du shadow de base quel que soit l'ordre CSS.
              className="!shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_20px_rgba(15,23,42,0.06)]"
              onSend={handleComposerSend}
              onStop={handleCancelStream}
              isLoading={shouldBlockCurrentComposer}
              placeholder={selectedCase ? `Posez une question sur "${selectedCase.title}"...` : "Posez une question juridique..."}
              initialInput={pendingPrompt ?? undefined}
              onDraftChange={onComposerDraftChange}
              onInitialInputConsumed={handlePromptConsumed}
              {...piecesJointesPosees}
              showDocumentSelector={!!selectedCaseId}
              documentContext={documentContext}
              onDocumentContextChange={setDocumentContext}
              showQuickActionsDropdown={false} // la grille d'accueil remplace le dropdown sur l'écran vide
              selectedCase={composerCase}
              availableCases={cases.map(c => ({ id: c.id, title: c.title }))}
              onCaseSelect={allowsHumanCaseSelection && selectedCaseId ? conversationCases.choisirDossier : undefined}
              {...composerRelatedCases}
              allowCaseClear={caseContextMode === 'selectable'}
            />
            </ComposerAccessSlot>
          </div>

          {/* Panneau actions rapides — Gestion de dossier / Analyse / Rédaction d'actes (refonte 2026-09-09) */}
          <QuickActionsPanel
            prompts={userPrompts}
            promptsLoading={promptsLoading}
            onPromptClick={handleUserPromptClick}
            onDeletePrompt={deletePrompt}
            onReorderPrompts={reorderPrompts}
            onCreatePrompt={createPrompt}
            onUpdatePrompt={updatePrompt}
            onSetPromptHidden={setPromptHidden}
            cases={cases.map(c => ({ id: c.id, title: c.title }))}
            selectedCaseId={selectedCaseId}
            onCaseSelect={conversationCases.choisirDossier}
          />
        </div>
      ) : showEmbeddedWelcome && embeddedEmptyState ? (
        /* Deux variantes d'accueil embarqué : le wizard garde son accueil centré,
           tandis qu'un hôte avec des actions fixes compose un seul groupe compact.
           Les actions restent ici (et non dans QuickActionsGrid) car elles sont
           des raccourcis métier de l'hôte, pas la bibliothèque personnelle éditable. */
        <div className={cn(
          "relative flex flex-1 flex-col min-h-0 items-center",
          showEmbeddedQuickActions ? "overflow-hidden px-4 py-3" : "px-6 py-6",
        )}>
          {showEmbeddedQuickActions ? (
            <>
              {/* The action list can shrink/scroll, but the composer must keep a
                  reserved slot so a zoomed or resized panel never hides its send controls. */}
              <section data-testid="embedded-dossier-starter" className="dossier-chat-quick-actions flex min-h-0 w-full max-w-lg flex-1 overflow-y-auto">
                <div className="flex min-h-full w-full flex-col items-center justify-center py-2">
                  <div className="dossier-chat-welcome-header flex flex-col items-center">
                    <img src="/logo_hector-black.png" alt="Hector" className="mb-4 h-11 w-auto opacity-80" />
                    <div className="mb-6 max-w-sm text-center">
                      <h1 className="text-xl font-semibold text-grey-900">
                        {embeddedEmptyState.heading ?? 'Comment puis-je vous aider ?'}
                      </h1>
                    </div>
                  </div>
                  <h3 className="mb-2 self-start text-xs font-semibold text-grey-700">Actions rapides</h3>
                  <div data-testid="embedded-quick-actions" className="dossier-chat-quick-actions-list flex w-full flex-col gap-2">
                    {embeddedQuickActions.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => handleEmbeddedQuickActionClick(action)}
                        aria-pressed={selectedEmbeddedQuickActionId === action.id}
                        className={cn(
                          "dossier-chat-action-card flex w-full items-center gap-3 rounded-lg border bg-white px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal focus-visible:ring-offset-2",
                          selectedEmbeddedQuickActionId === action.id
                            ? "border-violet-300 bg-violet-50/60"
                            : "border-grey-200 hover:border-grey-300 hover:bg-grey-50",
                        )}
                      >
                        {action.icon && (
                          <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-grey-100">
                            {action.icon}
                          </span>
                        )}
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-charcoal">{action.label}</span>
                          <span className="dossier-chat-action-description mt-0.5 block text-xs text-grey-600">{action.description}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
              <div data-testid="embedded-dossier-composer" className="mt-3 w-full max-w-lg flex-shrink-0">
                {embeddedEmptyComposer}
              </div>
            </>
          ) : (
            /*
              Le composer est ANCRÉ EN BAS et grandit vers le haut ; tout ce qui
              le précède (accueil + bloc de l'hôte) vit dans une zone qui scrolle.
              Avant, l'ensemble était un bloc centré : préremplir le composer le
              faisait grandir, poussait le tout vers le bas et sortait le champ de
              l'écran — l'avocat devait scroller la page pour retrouver le bouton
              envoyer, juste après avoir choisi sa source.

              La croissance est bornée par le composer lui-même (textarea
              `maxHeight`), qui scrolle en interne au-delà.
            */
            <div className="flex w-full max-w-2xl flex-1 min-h-0 flex-col justify-center">
              {/* Zone du dessus : dimensionnée par son contenu (`flex: 0 1 auto`,
                  donc PAS `flex-1`), elle est la seule à se comprimer quand le
                  composer grandit. Le couple accueil+composer reste centré tant
                  qu'il tient, et le composer garde toujours sa place. */}
              <div ref={embeddedWelcomeScrollRef} className="flex min-h-0 flex-col items-center overflow-y-auto">
                <div className="flex w-full flex-col items-center pb-3">
              {showEmbeddedDecorativeWelcome && (
                /* `@container` : l'accueil vit dans un panneau dont la largeur ne
                   suit pas le viewport. Sur un écran zoomé (ou un panneau étroit),
                   logo et titre se resserrent au lieu de manger la hauteur dont le
                   sélecteur et le composer ont besoin. Contient uniquement du
                   décor — surtout pas le composer, dont les menus se positionnent
                   en `fixed` et que `contain: layout` ancrerait ici. */
                <div className="@container flex w-full flex-col items-center">
                  {/* Même logo que /assistant (pas d'icône décorative maison). */}
                  <img src="/logo_hector-black.png" alt="Hector" className="mb-3 h-10 w-auto opacity-80 @md:mb-5 @md:h-14" />

                  <div className="text-center max-w-xl mb-4 @md:mb-6">
                    <h1 className="text-lg font-semibold text-grey-900 mb-1.5 @md:text-xl @md:mb-2">
                      {embeddedEmptyState.heading ?? 'Comment puis-je vous aider ?'}
                    </h1>
                    <div className="text-grey-500 text-sm h-5 overflow-hidden" aria-live="polite">
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.span
                          key={suggestedTaskIndex}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.35, ease: 'easeOut' }}
                          className="inline-block"
                        >
                          {rotatingTasks[suggestedTaskIndex % rotatingTasks.length]}
                        </motion.span>
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              )}

                  {/* Le bloc de l'hôte ferme sur sa propre charnière (« ou écrivez
                      votre demande ») : il reste donc collé au composer qu'il
                      annonce, et c'est le dernier élément de la zone scrollable. */}
                  {embeddedEmptyState.beforeComposer && (
                    <div className="w-full">{embeddedEmptyState.beforeComposer}</div>
                  )}
                </div>
              </div>

              <div className="w-full flex-shrink-0">
                {embeddedEmptyComposer}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Normal Chat Layout - same as GlobalChatInterface */
        <>
          {/* Title — top-left corner of the card, in its own fixed-height zone.
              Always rendered (even before the title resolves) so its appearance
              on refresh never pushes the conversation down. Left-aligned in the
              corner, independent of the centered message column. */}
          <div className={cn(
            // Fenêtre étroite : `pl-14` / `pr-14` laissent la place au bouton du menu des
            // conversations et à la pastille « Canvas » (icône seule), posés en absolu
            // par `AgentV3View` et qui recouvraient le titre.
            "flex-shrink-0 flex min-w-0 items-center gap-2 h-11 pl-14 pr-14 md:px-6",
            // Embarqué : le wizard porte déjà son propre titre d'étape.
            embedded && "hidden",
          )}>
            {!embedded && repereDeRetour}
            <h2 className="min-w-0 shrink text-sm font-semibold text-charcoal truncate">{conversationTitle}</h2>
            {/* Dossier rattaché à la conversation — chip cliquable qui ouvre le dossier.
                Masquée en embarqué : c'est le SEUL navigate() du composant, il
                éjecterait l'avocat du wizard vers la vue dossier. */}
            {!embedded && selectedCase && selectedCaseId && (
              <button
                type="button"
                onClick={() => navigate(`/cases/${selectedCaseId}`)}
                title={`Ouvrir le dossier « ${selectedCase.title} »`}
                // Masquée en fenêtre étroite (retour produit 2026-10-22) : le titre de
                // la conversation y avait à peine la place, et le dossier reste à
                // portée par la barre « Dossiers ».
                className="hidden max-w-[40%] flex-shrink-0 items-center gap-1 rounded-full border border-grey-200 px-2.5 py-0.5 text-xs text-grey-600 transition-colors hover:bg-[#F0F0EE] hover:text-charcoal md:flex"
              >
                <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{selectedCase.title}</span>
              </button>
            )}
            {!embedded && allExchanges.length > 0 && (
              <ConversationExportButton
                title={conversationTitle}
                exchanges={allExchanges}
                disabled={session.isStreaming || hasActiveTranscriptTurn}
              />
            )}
            {!embedded && restoreArchivedConversation && (
              <div className="ml-auto flex h-8 shrink-0 items-center">{restoreArchivedConversation}</div>
            )}
          </div>
          <div className="flex-1 min-w-0 overflow-y-auto" ref={scrollContainerRef}>
            {isEmptyLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center space-x-2 text-grey-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Chargement des messages...</span>
                </div>
              </div>
            ) : (
              <div ref={messagesContentRef} style={{ overflowAnchor: 'none' }}>
                {/* Unified exchange rendering — one code path for both
                    DB-restored history and live streaming exchanges. */}
                {allExchanges.map((exchange, exchangeIndex) => {
                  const exchangeCaseId = resolveDisplayExchangeCaseId(exchange, selectedCaseId);
                  return (
                    <ExchangeCitationProvider
                      key={exchange.id}
                      registry={citationRegistry}
                      caseId={exchangeCaseId}
                      conversationCaseId={selectedCaseId ?? undefined}
                      caseTitleOf={caseTitleOf}
                    >
                      <Fragment>
                        {exchange.userMessage && (
                          <div ref={(node) => registerExchangeAnchor(exchange.id, node)}>
                            <V3MessageList
                              messages={[exchange.userMessage]}
                              caseId={exchangeCaseId}
                              uploadingFilenames={exchange.phase !== 'history' ? uploadingFilenames : undefined}
                            />
                          </div>
                        )}
                        {(exchange.timeline.length > 0 || exchange.phase === 'live_pending' || exchange.phase === 'live_streaming') && (
                          <StreamingTimeline
                            canOpenCanvas={canOpenCanvas}
                            timeline={exchange.timeline}
                            phase={exchange.phase}
                            caseId={exchangeCaseId}
                            feedbackMessageId={exchange.lastAssistantMessageId}
                            feedbackInitial={exchange.lastAssistantMessageId ? feedbackMap.get(exchange.lastAssistantMessageId) : undefined}
                            onJurisprudenceResponse={canUseComposerActions && exchange.phase !== 'history' ? handleJurisprudenceResponse : undefined}
                            onJurisprudenceCancelled={canUseComposerActions && exchange.phase !== 'history' ? handleJurisprudenceCancellation : undefined}
                            onCaseWikiResetResponse={canUseComposerActions && exchange.phase !== 'history' ? handleCaseWikiResetResponse : undefined}
                            onDeletePiecesConfirmationResponse={canUseComposerActions && exchange.phase !== 'history' ? handleDeletePiecesConfirmationResponse : undefined}
                            onRedactionPlanSubmit={canUseComposerActions && exchange.phase !== 'history' ? session.sendRedactionPlanResponse : undefined}
                            onRedactionPlanCancel={canUseComposerActions && exchange.phase !== 'history' ? session.sendRedactionPlanCancellation : undefined}
                            onRedactionPlanDraftUpdate={canUseComposerActions && exchange.phase !== 'history' ? session.sendRedactionPlanDraftUpdate : undefined}
                            // Sorties agent : cliquables sur le DERNIER échange, quelle que
                            // soit sa phase.
                            //
                            // PAS de garde `phase !== 'history'` ici, contrairement aux
                            // callbacks de consentement ci-dessus : eux DOIVENT devenir inertes
                            // en historique (un clic périmé répondrait à un collector mort).
                            // Ces boutons-ci ne répondent à personne — navigation locale
                            // idempotente. Or `agent_done` commit le tour, qui passe aussitôt
                            // en `history` : avec la garde, les boutons apparaissaient pendant
                            // le streaming puis DISPARAISSAIENT à la seconde où l'agent
                            // finissait, càd exactement quand l'avocat allait cliquer
                            // (constaté en E2E le 2026-07-17).
                            //
                            // Restriction au dernier échange : rejouer « Passer au tamponnage »
                            // depuis un vieux message ferait sauter l'avocat d'étape sans
                            // rapport avec ce qu'il lit.
                            onUiAction={exchangeIndex === lastConversationExchangeIndex ? onUiAction : undefined}
                            uiActionCapabilities={exchange.uiActionCapabilities ?? []}
                            onSkillChangeResponse={canUseComposerActions && exchange.phase !== 'history' ? handleSkillChangeResponse : undefined}
                            onRetry={canUseComposerActions && exchange.phase !== 'history' ? handleRetry : undefined}
                            isLast={exchangeIndex === lastConversationExchangeIndex}
                            // Origine du timer de la plume = début du tour porté par la
                            // session (pas un Date.now() local) → correct après un switch
                            // de conversation. Seul l'exchange live l'utilise (timer caché
                            // en phase 'history').
                            turnStartedAt={exchange.phase !== 'history' ? session.turnStartedAt : null}
                          />
                        )}
                      </Fragment>
                    </ExchangeCitationProvider>
                  );
                })}
                {/* Bottom clearance so the last message sits above the fade overlay
                    at rest (otherwise its end would look faded). The fade then only
                    acts on text scrolling past, into the input zone. */}
                <div aria-hidden className="h-10" />
                {/* Scroll anchor — the browser keeps this in view as content grows above it */}
                <div ref={bottomSentinelRef} style={{ overflowAnchor: 'auto', height: 1 }} />
              </div>
            )}
          </div>

          <AnimatePresence>
            {showScrollToBottom && (
              <motion.div
                className="pointer-events-none absolute inset-x-0 bottom-28 z-20 flex justify-center"
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
              >
                <button
                  type="button"
                  aria-label="Revenir au dernier message"
                  title="Revenir au dernier message"
                  onClick={() => resumeBottomFollow('smooth')}
                  className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-grey-200 bg-white text-grey-700 shadow-[0_2px_8px_rgba(15,23,42,0.12)] transition-colors hover:bg-grey-50 hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue/40"
                >
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex-shrink-0 min-w-0 bg-white relative">
            {/* Soft fade so the conversation dissolves into the input zone as it
                scrolls past, instead of a hard cut (no separating line needed). */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-b from-transparent to-white"
            />
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 min-w-0">
              <ConnectionStatus
                hasSession={!!matchingActiveSessionKey}
                isConnected={session.isConnected}
                reconnectInfo={session.reconnectInfo}
                reconnectFailed={session.reconnectFailed}
                sessionStatus={session.status}
                onRetry={canUseComposerActions ? handleConnectionRetry : undefined}
              />
              <ComposerAccessSlot composerAccessState={composerAccessState} onRetry={onComposerAccessRetry} conversationCases={conversationCases.liste ?? []}>
              <div className="mb-2 flex justify-end">
                <CaseDiscoveryModelSelector />
              </div>
              <HectorPromptInputBox
                // Même ombre fine « flottante » qu'à l'écran d'accueil (cohérence,
                // demande user). L'ancien shadow-[0_8px_30px_rgba(0,0,0,0.12)] « dark
                // 4-sided blur » restait indésirable ici aussi ; cette version légère
                // (slate-900, 0.05/0.06) cohabite bien avec le fade blanc au-dessus.
                // `!` requis (cn() simple join, pas tailwind-merge).
                className="!shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_20px_rgba(15,23,42,0.06)]"
                compactHeight={embedded}
                memoryUsage={memoryUsage}
                // Rouverte, la conversation n'a pas de socket : sa mesure vient de la
                // jauge persistée ; jamais mesurée, le rond reste l'entrée de sa mémoire
                // dès qu'elle a un historique modifiable.
                showConversationMemory={memoryUsage !== null || canOfferCompaction}
                canCompactContext={canOfferCompaction}
                compaction={isOpeningSessionForCompaction ? RUNNING_CONTEXT_COMPACTION : session.compaction}
                onCompactContext={() => { void handleCompactContext(); }}
                onSend={handleComposerSend}
                onStop={handleCancelStream}
                isLoading={shouldBlockCurrentComposer}
                placeholder={selectedCase ? `Posez une question sur "${selectedCase.title}"...` : "Posez une question..."}
                initialInput={pendingPrompt ?? undefined}
                onDraftChange={onComposerDraftChange}
                onInitialInputConsumed={handlePromptConsumed}
                {...piecesJointesPosees}
                showDocumentSelector={!!selectedCaseId}
                documentContext={documentContext}
                onDocumentContextChange={setDocumentContext}
                showQuickActionsDropdown={!embedded} // chat actif : dropdown quick actions — MAIS pas en embarqué (raccourcis de recherche juridique hors-sujet dans le wizard « sans actions rapides »)
                quickActionPrompts={composerQuickActions}
                quickActionPromptsLoading={promptsLoading}
                onReorderPrompts={reorderPrompts}
                onDeletePrompt={deletePrompt}
                onCreatePrompt={createPrompt}
                onUpdatePrompt={updatePrompt}
                selectedCase={composerCase}
                availableCases={cases.map(c => ({ id: c.id, title: c.title }))}
                onCaseSelect={allowsHumanCaseSelection ? conversationCases.choisirDossier : undefined}
                {...composerRelatedCases}
                allowCaseClear={caseContextMode === 'selectable'}
              />
              </ComposerAccessSlot>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
